import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Users, CreditCard, Package as PackageIcon,
  TrendingUp, LogOut, Plus, Edit, CheckCircle, XCircle,
  Globe, ExternalLink, Copy, Eye, Store, Shield, ChevronRight, Settings, Menu, X, X as XIcon, ArrowRight, Briefcase, Trash2, Save, KeyRound, AlertTriangle, Database, RefreshCw, Upload, Image as ImageIcon, Calendar as CalendarIcon, Search, RotateCcw, Download, FileSpreadsheet, Activity, HeartPulse, Cpu, MemoryStick, Boxes, Ban, Unlock, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../lib/api';
import { confirmDialog } from '../components/ConfirmDialog';

const SA = `${API}/superadmin`;

export const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);

  const navItems = [
    { path: '/superadmin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/superadmin/tenants', icon: Store, label: 'Empresas' },
    { path: '/superadmin/plans', icon: PackageIcon, label: 'Planos' },
    { path: '/superadmin/business-types', icon: Briefcase, label: 'Tipos' },
    { path: '/superadmin/payments', icon: CreditCard, label: 'Financeiro' },
    { path: '/superadmin/users', icon: Users, label: 'Usuarios' },
    { path: '/superadmin/db-health', icon: Database, label: 'Saude do Banco' },
    { path: '/superadmin/purge', icon: Trash2, label: 'Limpeza de Dados' },
    { path: '/superadmin/adjustments-exports', icon: CalendarIcon, label: 'Ajustes e Exportacao' },
    { path: '/superadmin/backup', icon: Save, label: 'Backup & Restore' },
  ];

  const isActive = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0f0f23] flex" data-testid="superadmin-dashboard">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay" />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-50 w-72 bg-[#16162a] border-r border-[#2a2a4a] flex flex-col transform transition-transform duration-300 ease-in-out md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-[#2a2a4a] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <Shield size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white tracking-tight">8IP Admin</h1>
              <p className="text-xs text-violet-300">Painel de Controle</p>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden text-gray-400 hover:text-white p-1" data-testid="sidebar-close-btn">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => {
            const active = isActive(item.path, item.exact);
            return (
              <Link key={item.path} to={item.path}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 group ${active ? 'bg-violet-600/20 text-violet-300' : 'text-gray-400 hover:text-white hover:bg-[#1e1e3a]'}`}
              >
                <item.icon size={20} className={active ? 'text-violet-400' : 'text-gray-500 group-hover:text-violet-400'} />
                <span className="font-medium text-sm">{item.label}</span>
                {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-violet-400" />}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#2a2a4a]">
          <div className="flex items-center space-x-3 mb-3 px-2">
            <div className="w-8 h-8 rounded-full bg-violet-600/30 flex items-center justify-center">
              <span className="text-xs font-bold text-violet-300">{user?.name?.charAt(0) || 'S'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={() => setShowChangePw(true)}
            className="flex items-center space-x-3 px-4 py-2.5 rounded-xl text-gray-400 hover:text-violet-300 hover:bg-violet-500/10 transition-all w-full mb-1"
            data-testid="sa-open-change-pw"
          >
            <KeyRound size={18} />
            <span className="text-sm font-medium">Trocar Senha</span>
          </button>
          <button onClick={() => { logout(); navigate('/'); }}
            className="flex items-center space-x-3 px-4 py-2.5 rounded-xl text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all w-full"
          >
            <LogOut size={18} />
            <span className="text-sm font-medium">Sair</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto min-w-0">
        {/* Mobile header */}
        <div className="sticky top-0 z-30 md:hidden bg-[#0f0f23] border-b border-[#2a2a4a] px-4 py-3 flex items-center space-x-3">
          <button onClick={() => setSidebarOpen(true)} className="text-gray-400 hover:text-white p-1" data-testid="sidebar-toggle-btn">
            <Menu size={24} />
          </button>
          <h2 className="text-white font-bold text-lg">8IP Admin</h2>
        </div>
        <div className="max-w-7xl mx-auto p-4 md:p-8">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="tenants" element={<TenantsManagement />} />
            <Route path="plans" element={<PlansManagement />} />
            <Route path="business-types" element={<BusinessTypesManagement />} />
            <Route path="payments" element={<PaymentsManagement />} />
            <Route path="users" element={<SAUsersManagement />} />
            <Route path="db-health" element={<DBHealthPage />} />
            <Route path="purge" element={<TenantPurgePage />} />
            <Route path="sales-date-adjust" element={<SalesDateAdjustPage />} />
            <Route path="adjustments-exports" element={<AdjustmentsExportsPage />} />
            <Route path="backup" element={<BackupPage />} />
          </Routes>
        </div>
      </main>
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
};

/* ============ CHANGE PASSWORD (Super Admin) ============ */
const ChangePasswordModal = ({ onClose }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error('A nova senha deve ter ao menos 6 caracteres');
    if (next !== confirm) return toast.error('A confirmacao nao confere com a nova senha');
    setBusy(true);
    try {
      await axios.post(`${SA}/change-password`, { current_password: current, new_password: next }, { withCredentials: true });
      toast.success('Senha alterada com sucesso. Use a nova senha no proximo login.');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao trocar senha');
    } finally { setBusy(false); }
  };

  const inputClass = "w-full px-4 py-2.5 bg-[#1e1e3a] border border-[#2a2a4a] rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500";

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="sa-change-pw-modal">
      <div className="bg-[#16162a] border border-violet-500/30 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><KeyRound size={18} className="text-violet-400" /> Trocar Minha Senha</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Senha Atual *</label>
            <input type="password" value={current} onChange={e => setCurrent(e.target.value)} className={inputClass} required autoFocus data-testid="sa-current-pw" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Nova Senha *</label>
            <input type="password" value={next} onChange={e => setNext(e.target.value)} className={inputClass} required minLength={6} data-testid="sa-new-pw" />
            <p className="text-[10px] text-gray-500 mt-1">Minimo 6 caracteres. Sera armazenada criptografada (bcrypt).</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Confirme a Nova Senha *</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className={inputClass} required minLength={6} data-testid="sa-confirm-pw" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={busy} className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-violet-500 hover:to-indigo-500 disabled:opacity-50 transition-all flex items-center gap-1.5 text-sm" data-testid="sa-save-pw">
              <Save size={14} /> {busy ? 'Salvando...' : 'Salvar'}
            </button>
            <button type="button" onClick={onClose} className="bg-[#1e1e3a] text-gray-400 px-4 py-2 rounded-xl font-semibold hover:text-white text-sm">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ============ DASHBOARD HOME ============ */
const PlatformBrandingCard = () => {
  const [form, setForm] = useState({ name: '8IP', tagline: 'Sistema de Gestao', logo_url: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  useEffect(() => {
    axios.get(`${API}/platform/branding`).then(({ data }) => setForm(data)).catch(() => {});
  }, []);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, logo_url: data.url }));
      toast.success('Logo carregada — clique em Salvar para aplicar.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro no upload'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/platform/branding`, form, { withCredentials: true });
      toast.success('Branding da plataforma atualizado!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };
  const logoSrc = form.logo_url ? (form.logo_url.startsWith('http') ? form.logo_url : `${API.replace(/\/api$/, '')}${form.logo_url}`) : '';
  return (
    <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-6 mb-8" data-testid="platform-branding-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><ImageIcon size={18} className="text-violet-400" /> Branding da Plataforma</h2>
          <p className="text-xs text-gray-500 mt-1">Aparece no canto superior esquerdo da tela de login (ex.: <span className="text-violet-300 font-mono">8IP</span>).</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
        {/* Logo preview + upload */}
        <div className="flex flex-col items-center gap-3">
          <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-[#1e1e3a] to-[#0f0f23] border border-[#2a2a4a] p-3 flex items-center justify-center relative overflow-hidden">
            {logoSrc ? (
              <img src={logoSrc} alt="platform logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <div className="text-center text-gray-500 text-[10px]"><ImageIcon size={24} className="mx-auto mb-1 opacity-50" />Sem logo</div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="platform-logo-upload" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="text-xs font-bold text-violet-300 hover:text-violet-200 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-500/10 border border-violet-500/25 hover:bg-violet-500/15 transition-all disabled:opacity-50">
            <Upload size={12} /> {uploading ? 'Enviando...' : 'Trocar logo'}
          </button>
          {form.logo_url && <button onClick={() => setForm(f => ({ ...f, logo_url: '' }))} className="text-[10px] text-rose-400 hover:text-rose-300">Remover</button>}
        </div>
        {/* Fields */}
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Nome</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="8IP" data-testid="platform-name-input"
              className="w-full px-3 py-2 bg-[#1e1e3a] border border-[#2a2a4a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500" />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Tagline (linha secundaria)</label>
            <input value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })} placeholder="Sistema de Gestao" data-testid="platform-tagline-input"
              className="w-full px-3 py-2 bg-[#1e1e3a] border border-[#2a2a4a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">WhatsApp comercial (landing: "Falar com consultor")</label>
              <input value={form.whatsapp || ''} onChange={e => setForm({ ...form, whatsapp: e.target.value })} placeholder="5562999990000" data-testid="platform-whatsapp"
                className="w-full px-3 py-2 bg-[#1e1e3a] border border-[#2a2a4a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">E-mail comercial</label>
              <input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="comercial@8ip.com.br" data-testid="platform-email"
                className="w-full px-3 py-2 bg-[#1e1e3a] border border-[#2a2a4a] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500" />
            </div>
          </div>
          <div className="pt-2">
            <button onClick={save} disabled={saving} data-testid="platform-branding-save"
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white text-sm font-black shadow-lg shadow-violet-500/25 disabled:opacity-60 flex items-center gap-2 transition-all hover:-translate-y-0.5">
              <Save size={14} /> {saving ? 'Salvando...' : 'Salvar Branding'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const STATUS_STYLES = {
  ok:       { badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', dot: 'bg-emerald-400', title: 'Sistema Saudavel', desc: 'Backend respondendo normalmente.', icon: HeartPulse },
  warning:  { badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30',       dot: 'bg-amber-400',   title: 'Atencao — carga elevada', desc: 'Metricas do servidor perto do limite. Monitore os processos.', icon: AlertTriangle },
  critical: { badge: 'bg-rose-500/15 text-rose-300 border-rose-500/30',           dot: 'bg-rose-400',    title: 'Critico — risco de queda', desc: 'Servidor sob estresse. Reduza a carga imediatamente para evitar 520/524.', icon: AlertTriangle },
};

const formatUptime = (secs) => {
  if (!secs && secs !== 0) return '—';
  const s = Math.max(0, Math.floor(secs));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s % 60}s`;
};

const MetricBar = ({ label, value, unit, icon: Icon, thresholdWarn = 75, thresholdCrit = 90 }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const color = pct >= thresholdCrit ? 'bg-rose-500' : pct >= thresholdWarn ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="bg-[#1e1e3a] rounded-xl p-4 border border-[#2a2a4a]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {Icon && <Icon size={14} />}
          <span>{label}</span>
        </div>
        <span className="text-sm font-bold text-white">{value == null ? '—' : `${value.toFixed ? value.toFixed(1) : value}${unit || ''}`}</span>
      </div>
      <div className="h-2 rounded-full bg-[#0f0f23] overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const SystemHealthWidget = () => {
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  // iter156dl: modal com breakdown de processos e tenants
  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const loadDetail = async () => {
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/health/detail`, { withCredentials: true, timeout: 20000 });
      setDetail(data);
    } catch (e) {
      setDetail({ error: e?.response?.data?.detail || e?.message || 'Falha' });
    } finally {
      setDetailLoading(false);
    }
  };
  const openDetail = () => { setShowDetail(true); if (!detail || detailLoading === false) loadDetail(); };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/health`, { withCredentials: true, timeout: 8000 });
      setHealth(data);
      setError(null);
      setLastFetch(new Date());
    } catch (e) {
      setError(e?.message || 'Falha ao consultar /api/health');
      setLastFetch(new Date());
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000); // poll every 15s
    return () => clearInterval(id);
  }, []);

  const statusKey = error ? 'critical' : (health?.status || 'ok');
  const style = STATUS_STYLES[statusKey] || STATUS_STYLES.ok;
  const StatusIcon = style.icon;
  const sys = health?.system || {};
  const cpu = sys.cpu_percent;
  const mem = sys.memory_percent;
  const dbLat = health?.db?.latency_ms;
  const dbOk = !!health?.db?.ok;
  // iter156bx: helper para formatar MB → GB quando > 1024
  const fmtMem = (mb) => {
    if (mb == null) return '—';
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(0)} MB`;
  };

  return (
    <div className="mb-6" data-testid="system-health-widget">
      <div className={`bg-[#16162a] border rounded-2xl p-5 transition-colors ${statusKey === 'critical' ? 'border-rose-500/40' : statusKey === 'warning' ? 'border-amber-500/40' : 'border-[#2a2a4a]'}`}>
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${statusKey === 'critical' ? 'from-rose-500 to-red-600' : statusKey === 'warning' ? 'from-amber-500 to-orange-600' : 'from-emerald-500 to-green-600'} flex items-center justify-center`}>
              <StatusIcon size={20} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base md:text-lg font-bold text-white">{style.title}</h2>
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${style.badge}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${style.dot} animate-pulse`} />
                  {statusKey}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{error ? `Sem resposta do backend: ${error}` : style.desc}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right text-xs text-gray-500">
              <div>Uptime: <span className="text-gray-300 font-semibold">{formatUptime(health?.uptime_seconds)}</span></div>
              <div>Atualizado {lastFetch ? lastFetch.toLocaleTimeString('pt-BR') : '—'}</div>
            </div>
            {/* iter156dl: botao Ver Detalhes — abre breakdown de processos e tenants pesados */}
            <button
              onClick={openDetail}
              className="p-2 rounded-lg bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/40 text-violet-200 transition-colors"
              title="Ver processos e tenants que estao consumindo mais recursos"
              data-testid="system-health-detail-btn"
            >
              <ArrowRight size={14} />
            </button>
            <button
              onClick={load}
              className="p-2 rounded-lg bg-[#1e1e3a] hover:bg-[#252547] border border-[#2a2a4a] text-gray-300 transition-colors"
              title="Atualizar agora"
              data-testid="system-health-refresh"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <MetricBar label="CPU" value={cpu} unit="%" icon={Cpu} thresholdWarn={75} thresholdCrit={90} />
          <MetricBar label="Memoria" value={mem} unit="%" icon={MemoryStick} thresholdWarn={80} thresholdCrit={92} />
          <div className="bg-[#1e1e3a] rounded-xl p-4 border border-[#2a2a4a]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                <Database size={14} />
                <span>MongoDB</span>
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${dbOk ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${dbOk ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                {dbOk ? 'OK' : 'FAIL'}
              </span>
            </div>
            <div className="text-sm font-bold text-white">
              {dbLat != null ? `${dbLat} ms` : '—'}
              <span className="text-[10px] text-gray-500 font-normal ml-1">ping</span>
            </div>
          </div>
        </div>

        {/* iter156bx: Detalhes tecnicos do servidor — memoria total/CPU cores/disco/hostname */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]" data-testid="system-health-details">
          <div className="bg-[#0f0f23] border border-[#2a2a4a] rounded-lg p-2.5">
            <p className="text-gray-500 uppercase tracking-wider font-bold text-[9px]">{sys.scope === 'container' ? 'Memoria (limite do plano)' : 'Memoria Total'}</p>
            <p className="text-white font-black text-sm mt-0.5" data-testid="mem-total">{fmtMem(sys.memory_total_mb)}</p>
            <p className="text-gray-400 text-[10px] mt-0.5">Usado {fmtMem(sys.memory_used_mb)}{sys.memory_available_mb != null ? ` · Livre ${fmtMem(sys.memory_available_mb)}` : ''}</p>
          </div>
          <div className="bg-[#0f0f23] border border-[#2a2a4a] rounded-lg p-2.5">
            <p className="text-gray-500 uppercase tracking-wider font-bold text-[9px]">CPU</p>
            <p className="text-white font-black text-sm mt-0.5" data-testid="cpu-cores">{sys.cpu_count_logical ?? '—'} vCPU{sys.cpu_count_physical && sys.cpu_count_physical !== sys.cpu_count_logical ? ` (${sys.cpu_count_physical} fisicos)` : ''}</p>
            <p className="text-gray-400 text-[10px] mt-0.5">{sys.scope === 'container' ? 'Cota do container (plano)' : `Load 1m: ${sys.load_avg_1m ?? '—'}`}</p>
          </div>
          <div className="bg-[#0f0f23] border border-[#2a2a4a] rounded-lg p-2.5">
            <p className="text-gray-500 uppercase tracking-wider font-bold text-[9px]">Disco (raiz)</p>
            <p className="text-white font-black text-sm mt-0.5" data-testid="disk-total">{sys.disk_total_gb != null ? `${sys.disk_total_gb} GB` : '—'}</p>
            <p className="text-gray-400 text-[10px] mt-0.5">{sys.disk_used_gb != null ? `Usado ${sys.disk_used_gb} GB · ${sys.disk_percent?.toFixed?.(1) ?? sys.disk_percent}%` : '—'}</p>
          </div>
          <div className="bg-[#0f0f23] border border-[#2a2a4a] rounded-lg p-2.5">
            <p className="text-gray-500 uppercase tracking-wider font-bold text-[9px]">Processo Backend</p>
            <p className="text-white font-black text-sm mt-0.5" data-testid="proc-mem">{sys.process_memory_mb != null ? `${sys.process_memory_mb} MB` : '—'}</p>
            <p className="text-gray-400 text-[10px] mt-0.5">Threads {sys.process_threads ?? '—'} · Files {sys.process_open_files ?? '—'}</p>
          </div>
        </div>
        {sys.hostname && (
          <p className="mt-2 text-[10px] text-gray-500 font-mono" data-testid="system-hostname">host: {sys.hostname}{sys.swap_percent != null ? ` · swap ${sys.swap_percent.toFixed(0)}%` : ''}{sys.scope === 'container' ? ' · metricas do container (cgroup), nao do servidor fisico' : ''}</p>
        )}

        {statusKey !== 'ok' && (
          <div className={`mt-4 p-3 rounded-lg text-xs ${statusKey === 'critical' ? 'bg-rose-500/10 text-rose-200 border border-rose-500/30' : 'bg-amber-500/10 text-amber-200 border border-amber-500/30'}`}>
            <strong className="font-bold">Recomendacao:</strong>{' '}
            {statusKey === 'critical'
              ? 'Considere reiniciar o backend, verificar processos travados ou aumentar recursos do container. Emissoes SEFAZ e backups pesados devem ser evitados agora.'
              : 'Evite disparar tarefas pesadas (backups manuais, importacoes em lote) enquanto a carga estiver alta.'}
          </div>
        )}
      </div>

      {/* iter156dl: modal com detalhes — Top processos + tenants pesados */}
      {showDetail && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="system-detail-modal">
          <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#2a2a4a]">
              <div>
                <h3 className="text-lg font-bold text-white">Monitor Detalhado — Processos & Tenants</h3>
                <p className="text-xs text-gray-400 mt-0.5">Identifique gargalos de memoria e clientes com maior volume de dados.</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={loadDetail} className="p-2 rounded-lg bg-[#1e1e3a] hover:bg-[#252547] border border-[#2a2a4a] text-gray-300" title="Recarregar" data-testid="system-detail-reload">
                  <RefreshCw size={14} className={detailLoading ? 'animate-spin' : ''} />
                </button>
                <button onClick={() => setShowDetail(false)} className="p-2 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-200" data-testid="system-detail-close">
                  <XIcon size={14} />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1 p-5 space-y-6">
              {detailLoading && !detail && <p className="text-center text-gray-400 text-sm py-8">Carregando...</p>}
              {detail?.error && <p className="text-rose-300 text-sm bg-rose-500/10 border border-rose-500/30 rounded-lg p-3">{detail.error}</p>}
              {detail && !detail.error && (
                <>
                  {/* TOP PROCESSOS */}
                  <div>
                    <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <Cpu size={15} className="text-violet-400" />
                      Top 15 Processos por Memoria (RSS)
                    </h4>
                    <div className="bg-[#0f0f23] border border-[#2a2a4a] rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-[#1e1e3a] text-gray-400 uppercase text-[10px] font-bold">
                            <th className="px-3 py-2 text-left">PID</th>
                            <th className="px-3 py-2 text-left">Processo</th>
                            <th className="px-3 py-2 text-left">Usuario</th>
                            <th className="px-3 py-2 text-right">Memoria</th>
                            <th className="px-3 py-2 text-right">CPU%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.top_processes || []).map((p, i) => (
                            <tr key={i} className="border-t border-[#2a2a4a] hover:bg-[#1e1e3a]/50" data-testid={`proc-row-${i}`}>
                              <td className="px-3 py-2 text-gray-400 font-mono">{p.pid}</td>
                              <td className="px-3 py-2 text-white font-semibold">{p.name}</td>
                              <td className="px-3 py-2 text-gray-500">{p.user}</td>
                              <td className="px-3 py-2 text-right"><span className={`font-black ${p.memory_mb > 500 ? 'text-rose-300' : p.memory_mb > 200 ? 'text-amber-300' : 'text-emerald-300'}`}>{p.memory_mb} MB</span></td>
                              <td className="px-3 py-2 text-right text-gray-300">{p.cpu_percent}%</td>
                            </tr>
                          ))}
                          {(detail.top_processes || []).length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">Nao foi possivel listar processos (psutil indisponivel)</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* TOP TENANTS */}
                  <div>
                    <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <Database size={15} className="text-cyan-400" />
                      Top 20 Tenants por Volume de Dados
                    </h4>
                    <p className="text-[10px] text-gray-500 mb-2">Soma de documentos em produtos, vendas, financeiro, invoices, movimentacoes. Quanto maior o total, mais o tenant impacta memoria/queries.</p>
                    <div className="bg-[#0f0f23] border border-[#2a2a4a] rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-[#1e1e3a] text-gray-400 uppercase text-[10px] font-bold">
                            <th className="px-3 py-2 text-left">#</th>
                            <th className="px-3 py-2 text-left">Tenant</th>
                            <th className="px-3 py-2 text-center">Ativo</th>
                            <th className="px-3 py-2 text-right">Produtos</th>
                            <th className="px-3 py-2 text-right">Vendas</th>
                            <th className="px-3 py-2 text-right">Financeiro</th>
                            <th className="px-3 py-2 text-right">NF-e</th>
                            <th className="px-3 py-2 text-right">Movim.</th>
                            <th className="px-3 py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.top_tenants || []).map((t, i) => (
                            <tr key={t.tenant_id || i} className="border-t border-[#2a2a4a] hover:bg-[#1e1e3a]/50" data-testid={`tenant-row-${i}`}>
                              <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                              <td className="px-3 py-2">
                                <div className="text-white font-semibold">{t.name}</div>
                                <div className="text-[10px] text-gray-500">/{t.subdomain}</div>
                              </td>
                              <td className="px-3 py-2 text-center">
                                {t.active
                                  ? <span className="text-emerald-400">●</span>
                                  : <span className="text-rose-400" title="Tenant inativo">●</span>}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-300">{t.counts?.products ?? 0}</td>
                              <td className="px-3 py-2 text-right text-gray-300">{t.counts?.erp_sales ?? 0}</td>
                              <td className="px-3 py-2 text-right text-gray-300">{t.counts?.erp_financial ?? 0}</td>
                              <td className="px-3 py-2 text-right text-gray-300">{t.counts?.erp_invoices ?? 0}</td>
                              <td className="px-3 py-2 text-right text-gray-300">{t.counts?.erp_stock_movements ?? 0}</td>
                              <td className="px-3 py-2 text-right">
                                <span className={`font-black ${t.total > 5000 ? 'text-rose-300' : t.total > 1000 ? 'text-amber-300' : 'text-emerald-300'}`}>{t.total.toLocaleString('pt-BR')}</span>
                              </td>
                            </tr>
                          ))}
                          {(detail.top_tenants || []).length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">Nenhum tenant</td></tr>}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-2">Ideia rapida: se um tenant tiver &gt; 10k documentos no total, considere arquivar dados antigos ou aumentar recursos.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);
  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${SA}/dashboard/stats`, { withCredentials: true });
      setStats(data);
    } catch { toast.error('Erro ao carregar estatísticas'); }
    finally { setLoading(false); }
  };

  if (loading) return <Loader />;

  const cards = [
    { label: 'Total Empresas', value: stats?.total_tenants || 0, icon: Store, color: 'from-violet-500 to-indigo-600' },
    { label: 'Ativas', value: stats?.active_tenants || 0, icon: CheckCircle, color: 'from-emerald-500 to-green-600' },
    { label: 'Inativas', value: stats?.inactive_tenants || 0, icon: XCircle, color: 'from-red-500 to-rose-600' },
    { label: 'Receita Total', value: `R$ ${(stats?.total_revenue || 0).toFixed(2)}`, icon: TrendingUp, color: 'from-amber-500 to-orange-600' },
  ];

  return (
    <div>
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Dashboard</h1>
        <p className="text-gray-400 mt-1 text-sm">Visão geral da plataforma</p>
      </div>

      <SystemHealthWidget />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {cards.map((c, i) => (
          <div key={i} className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-5 hover:border-violet-500/30 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{c.label}</p>
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center`}>
                <c.icon size={18} className="text-white" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{c.value}</p>
          </div>
        ))}
      </div>

      <PlatformBrandingCard />

      <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">Empresas Recentes</h2>
          <Link to="/superadmin/tenants" className="text-sm text-violet-400 hover:text-violet-300 flex items-center space-x-1">
            <span>Ver todas</span><ChevronRight size={16} />
          </Link>
        </div>
        {stats?.recent_tenants?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_tenants.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-4 bg-[#1e1e3a] rounded-xl">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-600/20 flex items-center justify-center">
                    <Store size={18} className="text-violet-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">{t.company_name}</p>
                    <p className="text-xs text-gray-500">/{t.subdomain}/catalogo</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${t.license_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {t.license_active ? 'Ativa' : 'Inativa'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm text-center py-8">Nenhuma empresa cadastrada ainda</p>
        )}
      </div>
    </div>
  );
};

/* ============ TENANTS MANAGEMENT ============ */
const TenantsManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter77ct.94: busca por nome/subdomain/email/telefone
  const [search, setSearch] = useState('');

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, p, bt] = await Promise.all([
        axios.get(`${SA}/tenants`, { withCredentials: true }),
        axios.get(`${SA}/plans`, { withCredentials: true }),
        axios.get(`${SA}/business-types`, { withCredentials: true })
      ]);
      setTenants(t.data.data || []);
      setPlans(p.data.data || []);
      setBusinessTypes(bt.data.data || []);
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  };

  const toggleLicense = async (tenantId, currentStatus) => {
    try {
      await axios.put(`${SA}/tenants/${tenantId}/license`, { tenant_id: tenantId, active: !currentStatus }, { withCredentials: true });
      toast.success('Licença atualizada!');
      fetchAll();
    } catch { toast.error('Erro ao atualizar licença'); }
  };

  const toggleBlock = async (tenant) => {
    const blocking = !tenant.is_blocked;
    const reason = blocking
      ? window.prompt('Motivo do bloqueio financeiro:', 'Mensalidade em atraso')
      : window.prompt('Motivo do desbloqueio:', 'Pagamento regularizado');
    if (reason === null) return;
    try {
      await axios.put(`${SA}/tenants/${tenant.id}/block`, { blocked: blocking, reason }, { withCredentials: true });
      toast.success(blocking ? 'Empresa bloqueada por pendencia financeira' : 'Empresa desbloqueada');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao atualizar bloqueio'); }
  };

  const copyLink = (subdomain) => {
    // iter77ct.94: agora aponta para a tela de LOGIN do cliente (nao mais o /catalogo)
    const link = `${window.location.origin}/${subdomain}/login`;
    navigator.clipboard.writeText(link);
    toast.success('Link de acesso copiado!');
  };

  const getPlanName = (planId) => plans.find(p => p.id === planId)?.name || '-';
  const getBtName = (btId) => businessTypes.find(b => b.id === btId)?.name || '';
  const getBtCategory = (btId) => businessTypes.find(b => b.id === btId)?.category || 'catalogo';

  // iter77ct.94: filtro de busca (nome, subdomain, email, dono, telefone)
  const _norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filteredTenants = search.trim()
    ? tenants.filter(t => {
        const q = _norm(search);
        return _norm(t.company_name).includes(q)
          || _norm(t.subdomain).includes(q)
          || _norm(t.owner_email).includes(q)
          || _norm(t.owner_name).includes(q)
          || _norm(t.owner_phone).includes(q);
      })
    : tenants;

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Empresas</h1>
          <p className="text-gray-400 mt-1 text-sm">
            {filteredTenants.length} {filteredTenants.length === 1 ? 'empresa' : 'empresas'}
            {search.trim() ? ` encontrada${filteredTenants.length === 1 ? '' : 's'}` : ' cadastradas'}
            {search.trim() && tenants.length !== filteredTenants.length ? ` (de ${tenants.length})` : ''}
          </p>
        </div>
        <button onClick={() => { setEditingTenant(null); setShowForm(!showForm); }} data-testid="new-tenant-btn"
          className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-violet-500 hover:to-indigo-500 flex items-center justify-center space-x-2 transition-all shadow-lg shadow-violet-600/20 text-sm"
        >
          <Plus size={18} />
          <span>Nova Empresa</span>
        </button>
      </div>

      {/* iter77ct.94: barra de busca */}
      <div className="mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar empresa por nome, slug, e-mail, dono, telefone..."
            className="w-full pl-9 pr-3 py-2.5 bg-[#16162a] border border-[#2a2a4a] rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
            data-testid="tenant-search-input"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white" data-testid="tenant-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {showForm && <TenantForm tenant={editingTenant} plans={plans} businessTypes={businessTypes} onClose={() => { setShowForm(false); setEditingTenant(null); fetchAll(); }} />}

      {tenants.length === 0 ? (
        <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-12 text-center">
          <Store size={48} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Nenhuma empresa ainda</h3>
          <p className="text-gray-500 text-sm mb-6">Crie a primeira empresa para seus clientes</p>
          <button onClick={() => setShowForm(true)} className="bg-violet-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-violet-500">
            Criar Primeira Empresa
          </button>
        </div>
      ) : filteredTenants.length === 0 ? (
        <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-8 text-center" data-testid="tenant-search-empty">
          <Search size={36} className="mx-auto text-gray-600 mb-3" />
          <h3 className="text-base font-semibold text-white mb-1">Nenhuma empresa encontrada</h3>
          <p className="text-gray-500 text-sm">Ajuste o termo da busca ou <button onClick={() => setSearch('')} className="text-violet-400 hover:underline">limpe o filtro</button>.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filteredTenants.map((tenant) => (
            <div key={tenant.id} className="bg-[#16162a] border border-[#2a2a4a] rounded-xl p-3.5 hover:border-violet-500/30 transition-colors" data-testid={`tenant-${tenant.subdomain}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start space-x-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <Store size={18} className="text-violet-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-white text-sm sm:text-base truncate">{tenant.company_name}</h3>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                      <span className="text-[11px] text-gray-500 flex items-center space-x-1">
                        <Users size={10} /><span className="truncate">{tenant.owner_name}</span>
                      </span>
                      <span className="text-[11px] text-gray-500 truncate">{tenant.owner_email}</span>
                      {tenant.owner_phone && <span className="text-[11px] text-gray-500 truncate">{tenant.owner_phone}</span>}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${tenant.license_active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {tenant.license_active ? 'Ativa' : 'Inativa'}
                      </span>
                      {tenant.is_blocked && (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-black bg-rose-600/25 text-rose-300 border border-rose-500/40 flex items-center gap-1" title={tenant.blocked_reason || ''} data-testid={`tenant-blocked-badge-${tenant.subdomain}`}>
                          <Ban size={9} /> Bloqueada (financeiro)
                        </span>
                      )}
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-violet-500/15 text-violet-400">
                        {getPlanName(tenant.plan_id)}
                      </span>
                      {getBtName(tenant.business_type_id) && (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-500/15 text-blue-400 flex items-center space-x-1">
                          <Briefcase size={9} /><span>{getBtName(tenant.business_type_id)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1.5 flex-shrink-0">
                  <button onClick={() => { setEditingTenant(tenant); setShowForm(true); }} className="p-1.5 rounded-md hover:bg-violet-500/20 text-gray-500 hover:text-violet-400 transition-colors" title="Editar empresa" data-testid={`edit-tenant-${tenant.subdomain}`}>
                    <Edit size={14} />
                  </button>
                  <button onClick={() => toggleBlock(tenant)} className={`p-1.5 rounded-md transition-colors ${tenant.is_blocked ? 'bg-rose-600/20 text-rose-300 hover:bg-emerald-600/20 hover:text-emerald-300' : 'text-gray-500 hover:bg-rose-500/20 hover:text-rose-400'}`} title={tenant.is_blocked ? 'Desbloquear (financeiro)' : 'Bloquear por pendencia financeira'} data-testid={`block-tenant-${tenant.subdomain}`}>
                    {tenant.is_blocked ? <Unlock size={14} /> : <Ban size={14} />}
                  </button>
                  <label className="relative inline-flex items-center cursor-pointer" title="Ativar/Desativar licenca">
                    <input type="checkbox" checked={tenant.license_active} onChange={() => toggleLicense(tenant.id, tenant.license_active)} className="sr-only peer" />
                    <div className="w-9 h-5 bg-gray-700 rounded-full peer peer-checked:bg-emerald-600 peer-checked:after:translate-x-4 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                  </label>
                </div>
              </div>

              {/* License usage block */}
              {(tenant.max_users || tenant.max_products || tenant.max_companies || tenant.monthly_value) && (
                <div className="mt-2.5 grid grid-cols-2 md:grid-cols-4 gap-1.5" data-testid={`tenant-usage-${tenant.subdomain}`}>
                  {(() => {
                    const usage = tenant.usage || { users: 0, products: 0, companies: 0 };
                    const items = [
                      { key: 'users', label: 'Usuarios', used: usage.users, max: tenant.max_users },
                      { key: 'products', label: 'Produtos', used: usage.products, max: tenant.max_products },
                      { key: 'companies', label: 'Filiais adic.', used: usage.companies, max: tenant.max_companies },
                    ];
                    return (
                      <>
                        {items.map(it => {
                          if (!it.max) return null;
                          const pct = Math.min(100, (it.used / it.max) * 100);
                          const over = it.used >= it.max;
                          const near = pct >= 80;
                          const color = over ? 'from-rose-600 to-red-600' : near ? 'from-amber-500 to-orange-500' : 'from-emerald-500 to-teal-600';
                          const txt = over ? 'text-rose-300' : near ? 'text-amber-300' : 'text-emerald-300';
                          return (
                            <div key={it.key} className="bg-[#1e1e3a] rounded-md px-2 py-1" data-testid={`tenant-usage-${tenant.subdomain}-${it.key}`}>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[9px] font-bold text-gray-500 uppercase">{it.label}</span>
                                <span className={`text-[10px] font-black ${txt}`}>{it.used}/{it.max}</span>
                              </div>
                              <div className="w-full bg-[#0f0f1e] rounded-full h-0.5 overflow-hidden">
                                <div className={`h-full bg-gradient-to-r ${color}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                        {tenant.monthly_value && (
                          <div className="bg-gradient-to-br from-violet-900/40 to-indigo-900/30 rounded-md px-2 py-1 border border-violet-500/20" data-testid={`tenant-monthly-${tenant.subdomain}`}>
                            <div className="text-[9px] font-bold text-violet-300 uppercase mb-0">Mensalidade</div>
                            <div className="text-xs font-black text-white">R$ {Number(tenant.monthly_value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              <div className="mt-2.5 pt-2.5 border-t border-[#2a2a4a] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div className="flex items-center space-x-2 bg-[#1e1e3a] rounded-md px-2.5 py-1.5 w-full sm:w-auto overflow-hidden">
                  <Globe size={12} className="text-gray-500 flex-shrink-0" />
                  <code className="text-[11px] text-violet-300 truncate">/{tenant.subdomain}/login</code>
                  <button onClick={() => copyLink(tenant.subdomain)} className="text-gray-500 hover:text-violet-400 transition-colors flex-shrink-0" title="Copiar link de login" data-testid={`copy-login-${tenant.subdomain}`}>
                    <Copy size={12} />
                  </button>
                </div>
                <div className="flex items-center space-x-1.5">
                  <a href={`/${tenant.subdomain}/catalogo`} target="_blank" rel="noreferrer"
                    className="flex items-center space-x-1 px-2.5 py-1.5 rounded-md bg-violet-600/15 text-violet-400 text-[11px] font-medium hover:bg-violet-600/25 transition-colors"
                  >
                    <Eye size={12} /><span>Catalogo</span>
                  </a>
                  <a href={`/${tenant.subdomain}/dashboard`}
                    className="flex items-center space-x-1 px-2.5 py-1.5 rounded-md bg-teal-600/15 text-teal-400 text-[11px] font-medium hover:bg-teal-600/25 transition-colors"
                  >
                    <Settings size={12} /><span>Gestao</span>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ============ TENANT FORM (Create/Edit) ============ */
const TenantCompaniesTab = ({ tenantId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${SA}/tenants/${tenantId}/companies?all=1`, { withCredentials: true });
      setRows(data.data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (c) => {
    const disabling = !c.disabled_by_superadmin;
    if (disabling && !window.confirm(`Desativar a filial "${c.code ? c.code + ' · ' : ''}${c.name}"?\n\nEla some de todos os seletores do sistema do cliente e aparece esmaecida no cadastro de Filiais. Vendas, estoque e financeiro ja lancados sao preservados.`)) return;
    setBusy(c.id);
    try {
      await axios.put(`${SA}/tenants/${tenantId}/companies/${c.id}/disable`, { disabled: disabling, reason: disabling ? 'Desativada pelo Super Admin' : 'Reativada pelo Super Admin' }, { withCredentials: true });
      toast.success(disabling ? 'Filial desativada' : 'Filial reativada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao atualizar filial'); }
    finally { setBusy(null); }
  };
  const activeCount = rows.filter(r => !r.disabled_by_superadmin).length;
  return (
    <div className="bg-gradient-to-br from-sky-950/40 to-cyan-950/20 border border-sky-500/20 rounded-xl p-4 mb-5" data-testid="tenant-companies-block">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-bold text-sky-200 flex items-center gap-2"><Building2 size={15} /> Filiais cadastradas pelo cliente</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{rows.length} filial(is) · {activeCount} ativa(s). Desativar oculta a filial em todo o sistema do cliente (seletor de filial, PDV, relatorios); no cadastro de Filiais ela aparece esmaecida.</p>
        </div>
        <button type="button" onClick={load} className="text-xs text-sky-300 hover:text-white" data-testid="tenant-companies-refresh">Atualizar</button>
      </div>
      {loading ? <p className="text-xs text-gray-500">Carregando...</p> : rows.length === 0 ? (
        <p className="text-xs text-gray-500" data-testid="tenant-companies-empty">Nenhuma filial cadastrada ainda.</p>
      ) : (
        <div className="space-y-2">
          {rows.map(c => (
            <div key={c.id} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${c.disabled_by_superadmin ? 'border-[#2a2a4a] bg-[#12122a] opacity-60' : 'border-sky-500/20 bg-[#1a1a34]'}`} data-testid={`tenant-company-row-${c.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">
                  {c.code && <span className="text-sky-300 mr-1.5">{c.code}</span>}{c.name}
                  {c.is_matriz && <span className="ml-2 px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 text-[9px] font-black uppercase">Matriz</span>}
                  {c.disabled_by_superadmin && <span className="ml-2 px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 text-[9px] font-black uppercase" data-testid={`tenant-company-disabled-${c.id}`}>Desativada</span>}
                  {!c.disabled_by_superadmin && !c.active && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300 text-[9px] font-black uppercase">Inativa (cliente)</span>}
                </p>
                <p className="text-[11px] text-gray-500 truncate">{c.cnpj || 'sem CNPJ'}{c.city ? ` · ${c.city}${c.uf ? '/' + c.uf : ''}` : ''}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0" title={c.disabled_by_superadmin ? 'Reativar filial' : 'Desativar filial'}>
                <input type="checkbox" checked={!c.disabled_by_superadmin} disabled={busy === c.id} onChange={() => toggle(c)} className="sr-only peer" data-testid={`tenant-company-toggle-${c.id}`} />
                <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500" />
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TenantForm = ({ tenant, plans, businessTypes, onClose }) => {
  const [form, setForm] = useState({
    company_name: tenant?.company_name || '',
    owner_name: tenant?.owner_name || '',
    owner_email: tenant?.owner_email || '',
    owner_phone: tenant?.owner_phone || '',
    subdomain: tenant?.subdomain || '',
    plan_id: tenant?.plan_id || '',
    business_type_id: tenant?.business_type_id || '',
    cnpj: tenant?.cnpj || '',
    address: tenant?.address || '',
    max_users: tenant?.max_users ?? '',
    max_products: tenant?.max_products ?? '',
    max_companies: tenant?.max_companies ?? '',
    monthly_value: tenant?.monthly_value ?? '',
    // iter156ez: composicao da mensalidade
    matriz_value: tenant?.matriz_value ?? '',
    branch_value: tenant?.branch_value ?? '',
    discount_value: tenant?.discount_value ?? '',
    // iter156fh: cobranca (integracao AgentCRM)
    billing_day: tenant?.billing_day ?? 10,
    billing_contact_name: tenant?.billing_contact_name || '',
    billing_contact_email: tenant?.billing_contact_email || '',
    billing_contact_phone: tenant?.billing_contact_phone || '',
    billing_notes: tenant?.billing_notes || '',
    cashback_enabled: !!tenant?.cashback_enabled,
    // iter156dz: Ordens de Servico / Locacao — flag per-tenant
    os_module_enabled: !!tenant?.os_module_enabled,
    // iter156ea: label customizado do modulo OS (default "Ordem de Servico")
    os_module_label: tenant?.os_module_label || '',
    // iter156av: flags per-tenant desativadas na UI (agora so via Tipo de Negocio).
    // Preservamos os valores existentes para backward-compat (tenants antigos que
    // tinham per-tenant enabled continuam funcionando via OR no backend).
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmDiff, setConfirmDiff] = useState(null); // {diff: [{field,before,after}], payload}
  const [formTab, setFormTab] = useState('dados'); // iter156ez: 'dados' | 'modulos' | 'licencas'
  const num = (v) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return isFinite(n) ? n : 0; };
  const hasComposition = num(form.matriz_value) > 0 || num(form.branch_value) > 0;
  const computedMonthly = Math.max(0, num(form.matriz_value) + num(form.branch_value) * (parseInt(form.max_companies, 10) || 0) - num(form.discount_value));
  const fmtR = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // Build a diff of changed fields vs the original tenant (only used in edit mode).
  const buildDiff = () => {
    if (!tenant) return [];
    const fields = [
      ['company_name', 'Nome da Empresa'],
      ['owner_name', 'Responsavel'],
      ['owner_email', 'E-mail'],
      ['owner_phone', 'Telefone'],
      ['plan_id', 'Plano'],
      ['business_type_id', 'Tipo de Negocio'],
      ['cnpj', 'CNPJ'],
      ['address', 'Endereco'],
      ['max_users', 'Max Usuarios'],
      ['max_products', 'Max Produtos'],
      ['max_companies', 'Filiais Adicionais'],
      ['matriz_value', 'Valor Matriz'],
      ['branch_value', 'Valor Filial'],
      ['discount_value', 'Desconto'],
      ['monthly_value', 'Mensalidade'],
      ['billing_day', 'Dia de vencimento'],
      ['billing_contact_name', 'Contato cobranca'],
      ['billing_contact_email', 'E-mail cobranca'],
      ['billing_contact_phone', 'WhatsApp cobranca'],
      ['billing_notes', 'Obs. cobranca'],
    ];
    const out = [];
    const effective = { ...form, ...(hasComposition ? { monthly_value: computedMonthly } : {}) };
    for (const [k, label] of fields) {
      const before = tenant[k];
      let after = effective[k];
      // Normalize empty strings vs null/undefined for comparison
      const norm = (v) => (v === '' || v == null) ? '' : String(v);
      if (norm(before) !== norm(after)) {
        // Human-readable for select fields
        if (k === 'plan_id') {
          out.push({ key: k, label, before: plans.find(p => p.id === before)?.name || '—', after: plans.find(p => p.id === after)?.name || '—' });
        } else if (k === 'business_type_id') {
          out.push({ key: k, label, before: businessTypes.find(b => b.id === before)?.name || '—', after: businessTypes.find(b => b.id === after)?.name || '—' });
        } else {
          out.push({ key: k, label, before: norm(before) || '—', after: norm(after) || '—' });
        }
      }
    }
    return out;
  };

  const buildPayload = () => {
    const payload = { ...form };
    if (!payload.business_type_id) payload.business_type_id = null;
    ['max_users','max_products','max_companies'].forEach(k => {
      payload[k] = payload[k] === '' || payload[k] == null ? null : parseInt(payload[k], 10);
    });
    // iter156ez: valores monetarios da composicao — vazio vira 0 (backend ignora null e nao limparia o campo)
    ['matriz_value', 'branch_value', 'discount_value'].forEach(k => { payload[k] = num(payload[k]); });
    payload.billing_day = Math.max(1, Math.min(31, parseInt(payload.billing_day, 10) || 10));
    payload.monthly_value = hasComposition
      ? computedMonthly
      : (payload.monthly_value === '' || payload.monthly_value == null ? null : parseFloat(String(payload.monthly_value).replace(',', '.')));
    return payload;
  };

  const persist = async (payload) => {
    setSubmitting(true);
    try {
      if (tenant) {
        await axios.put(`${SA}/tenants/${tenant.id}`, payload, { withCredentials: true });
        toast.success(`Empresa "${form.company_name}" atualizada!`);
      } else {
        const { data } = await axios.post(`${SA}/tenants`, payload, { withCredentials: true });
        toast.success(`Empresa "${form.company_name}" criada! Senha do admin: ${data.admin_password || 'veja no e-mail'}`);
      }
      setConfirmDiff(null);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar empresa');
    } finally { setSubmitting(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.plan_id) { toast.error('Selecione um plano'); return; }
    const payload = buildPayload();
    // In EDIT mode, if there are changes, ask for explicit confirmation showing
    // the before/after diff — this is the safeguard that prevents accidental
    // overwrites of other tenants' data (e.g., renaming VestStock to "Rei Outlet").
    if (tenant) {
      const diff = buildDiff();
      if (diff.length === 0) { toast.info('Nenhuma alteracao para salvar'); return; }
      setConfirmDiff({ diff, payload });
      return;
    }
    await persist(payload);
  };

  const inputClass = "w-full px-4 py-2.5 bg-[#1e1e3a] border border-[#2a2a4a] rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500";

  return (
    <form onSubmit={handleSubmit} className="bg-[#16162a] border border-violet-500/30 rounded-2xl p-6 mb-6" data-testid="tenant-form">
      <h2 className="text-xl font-bold text-white mb-4">{tenant ? 'Editar Empresa' : 'Criar Nova Empresa'}</h2>
      {/* iter156ez: abas do formulario */}
      <div className="flex gap-1 mb-5 border-b border-[#2a2a4a]" data-testid="tenant-form-tabs">
        {[['dados', 'Dados da Empresa'], ['modulos', 'Modulos'], ['licencas', 'Licencas & Cobranca'], ...(tenant ? [['filiais', 'Filiais']] : [])].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFormTab(k)} data-testid={`tenant-tab-${k}`}
            className={`px-4 py-2 text-sm font-bold rounded-t-lg transition-colors ${formTab === k ? 'text-violet-300 border-b-2 border-violet-500 bg-violet-500/10' : 'text-gray-500 hover:text-gray-300'}`}>{l}</button>
        ))}
      </div>
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 ${formTab === 'dados' ? '' : 'hidden'}`}>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Nome da Empresa *</label>
          <input type="text" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Ex: VestStock" className={inputClass} required data-testid="tenant-company-name" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Responsavel *</label>
          <input type="text" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Nome do dono" className={inputClass} required data-testid="tenant-owner-name" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">E-mail *</label>
          <input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="email@empresa.com" className={inputClass} required={!tenant} data-testid="tenant-email" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Telefone *</label>
          <input type="tel" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} placeholder="(00) 00000-0000" className={inputClass} required={!tenant} data-testid="tenant-phone" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider flex items-center gap-1">
            Slug da Empresa *
            {!!tenant && <span className="text-[9px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full normal-case font-bold">Imutavel</span>}
          </label>
          <div className="relative">
            <input
              type="text"
              value={form.subdomain}
              onChange={(e) => setForm({ ...form, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
              placeholder="minha-empresa"
              className={`${inputClass} ${!!tenant ? 'opacity-50 cursor-not-allowed' : ''}`}
              required
              disabled={!!tenant}
              data-testid="tenant-subdomain"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {!!tenant
              ? <>O slug nao pode ser alterado apos a criacao (preserva o roteamento). Acesso: <span className="text-violet-400">/{form.subdomain}/catalogo</span></>
              : <>Acesso: <span className="text-violet-400">/{form.subdomain || '...'}/catalogo</span></>}
          </p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Plano *</label>
          <select value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })} className={inputClass} required data-testid="tenant-plan">
            <option value="">Selecione um plano</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name} — R$ {p.price.toFixed(2)}/{p.billing_cycle}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Tipo de Negocio</label>
          <select value={form.business_type_id} onChange={(e) => setForm({ ...form, business_type_id: e.target.value })} className={inputClass} data-testid="tenant-business-type">
            <option value="">Nenhum (padrao)</option>
            {businessTypes.map((bt) => (
              <option key={bt.id} value={bt.id}>{bt.name}</option>
            ))}
          </select>
          <p className="text-[10px] text-gray-600 mt-1">Define os campos de produto exibidos no catalogo</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">CNPJ</label>
          <input type="text" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} placeholder="00.000.000/0000-00" className={inputClass} data-testid="tenant-cnpj" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Endereco</label>
          <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Endereco completo" className={inputClass} data-testid="tenant-address" />
        </div>
      </div>

      {/* iter156ea: Modulo de OS / Locacao — habilitacao + nome customizado */}
      <div className={`bg-gradient-to-br from-amber-950/40 to-orange-950/20 border border-amber-500/20 rounded-xl p-4 mb-5 ${formTab === 'modulos' ? '' : 'hidden'}`} data-testid="tenant-os-module-block">
        <h3 className="text-sm font-bold text-amber-300 mb-1 uppercase tracking-wider">Modulo Ordem de Servico / Locacao</h3>
        <p className="text-[11px] text-gray-500 mb-3">Ativa o modulo de OS para este tenant. O nome do modulo pode ser customizado (ex: "Ordem de Locacao", "Ordem de Manutencao"). O nome escolhido propaga para o menu, tela e perfil de acesso.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="flex items-center gap-2 text-sm text-amber-200 cursor-pointer">
              <input type="checkbox" checked={!!form.os_module_enabled} onChange={e => setForm({ ...form, os_module_enabled: e.target.checked })} data-testid="tenant-os-enabled" />
              <span className="font-bold">Habilitar Modulo OS</span>
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Nome do Modulo (opcional)</label>
            <input type="text" value={form.os_module_label} onChange={e => setForm({ ...form, os_module_label: e.target.value })} placeholder="Padrao: Ordem de Servico" disabled={!form.os_module_enabled} className={`${inputClass} ${!form.os_module_enabled ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="tenant-os-label" />
            <p className="text-[10px] text-gray-500 mt-1">Deixe vazio para usar "Ordem de Servico". Preencha para nomes como "Ordem de Locacao", "Ordem de Manutencao", etc.</p>
          </div>
        </div>
      </div>

      {/* License & billing block — iter156ez: aba propria + composicao matriz/filiais/desconto */}
      <div className={`bg-gradient-to-br from-violet-950/40 to-indigo-950/20 border border-violet-500/20 rounded-xl p-4 mb-5 ${formTab === 'licencas' ? '' : 'hidden'}`} data-testid="tenant-license-block">
        <h3 className="text-sm font-bold text-violet-300 mb-1 uppercase tracking-wider">Licencas & Cobranca</h3>
        <p className="text-[11px] text-gray-500 mb-4">Defina os limites contratados. Deixar em branco = ilimitado. Ao exceder, o usuario sera bloqueado e instruido a contatar a administracao da 8iP. A primeira empresa cadastrada e a <b className="text-gray-300">matriz</b>; "Filiais adicionais" sao as empresas extras permitidas e multiplicam o valor da filial.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Total Usuarios</label>
            <input type="number" min="0" value={form.max_users} onChange={(e) => setForm({ ...form, max_users: e.target.value })} placeholder="Ilimitado" className={inputClass} data-testid="tenant-max-users" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Total Produtos</label>
            <input type="number" min="0" value={form.max_products} onChange={(e) => setForm({ ...form, max_products: e.target.value })} placeholder="Ilimitado" className={inputClass} data-testid="tenant-max-products" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Filiais Adicionais</label>
            <input type="number" min="0" value={form.max_companies} onChange={(e) => setForm({ ...form, max_companies: e.target.value })} placeholder="0 = so matriz" className={inputClass} data-testid="tenant-max-companies" />
            <p className="text-[10px] text-gray-600 mt-1">Alem da matriz. Total de empresas permitidas: {1 + (parseInt(form.max_companies, 10) || 0)}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Valor Matriz (R$)</label>
            <input type="text" inputMode="decimal" value={form.matriz_value} onChange={(e) => setForm({ ...form, matriz_value: e.target.value })} placeholder="0,00" className={inputClass} data-testid="tenant-matriz-value" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Valor Filial (R$)</label>
            <input type="text" inputMode="decimal" value={form.branch_value} onChange={(e) => setForm({ ...form, branch_value: e.target.value })} placeholder="0,00" className={inputClass} data-testid="tenant-branch-value" />
            <p className="text-[10px] text-gray-600 mt-1">x {parseInt(form.max_companies, 10) || 0} filial(is) = {fmtR(num(form.branch_value) * (parseInt(form.max_companies, 10) || 0))}</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Desconto (R$)</label>
            <input type="text" inputMode="decimal" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} placeholder="0,00" className={inputClass} data-testid="tenant-discount-value" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Valor Mensal (R$)</label>
            {hasComposition ? (
              <div className="px-4 py-2.5 bg-violet-600/20 border border-violet-500/40 rounded-xl text-violet-100 text-sm font-black tabular-nums" data-testid="tenant-monthly-value-computed">{fmtR(computedMonthly)}</div>
            ) : (
              <input type="text" inputMode="decimal" value={form.monthly_value} onChange={(e) => setForm({ ...form, monthly_value: e.target.value })} placeholder="0.00" className={inputClass} data-testid="tenant-monthly-value" />
            )}
            <p className="text-[10px] text-gray-600 mt-1">{hasComposition ? 'Matriz + Filiais x Qtd - Desconto' : 'Preencha matriz/filial para calcular automaticamente'}</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Dia de vencimento</label>
            <input type="number" min="1" max="31" value={form.billing_day} onChange={(e) => setForm({ ...form, billing_day: e.target.value })} placeholder="10" className={inputClass} data-testid="tenant-billing-day" />
            <p className="text-[10px] text-gray-600 mt-1">Dia do mes em que a mensalidade vence (enviado ao AgentCRM)</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-violet-500/20" data-testid="tenant-billing-contact-block">
          <p className="text-xs font-bold text-violet-200 mb-2">Contato de cobranca <span className="text-gray-500 font-normal">(opcional — vazio usa o responsavel da empresa)</span></p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input type="text" value={form.billing_contact_name} onChange={(e) => setForm({ ...form, billing_contact_name: e.target.value })} placeholder="Nome" className={inputClass} data-testid="tenant-billing-contact-name" />
            <input type="email" value={form.billing_contact_email} onChange={(e) => setForm({ ...form, billing_contact_email: e.target.value })} placeholder="E-mail" className={inputClass} data-testid="tenant-billing-contact-email" />
            <input type="text" value={form.billing_contact_phone} onChange={(e) => setForm({ ...form, billing_contact_phone: e.target.value })} placeholder="WhatsApp (com DDD)" className={inputClass} data-testid="tenant-billing-contact-phone" />
          </div>
          <textarea value={form.billing_notes} onChange={(e) => setForm({ ...form, billing_notes: e.target.value })} placeholder="Observacoes de cobranca (ex.: pagar via Pix, boleto enviado ao financeiro...)" rows={2} className={`${inputClass} mt-3`} data-testid="tenant-billing-notes" />
          {tenant?.is_blocked && (
            <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3" data-testid="tenant-form-blocked-banner">
              <div className="text-xs text-rose-200">
                <p className="font-black">Bloqueada (financeiro)</p>
                <p className="text-rose-300/80 mt-0.5">{tenant.blocked_reason || 'Pendencia financeira'}{tenant.blocked_at ? ` · ${new Date(tenant.blocked_at).toLocaleString('pt-BR')}` : ''}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {tenant && (
        <div className={formTab === 'filiais' ? '' : 'hidden'}>
          <TenantCompaniesTab tenantId={tenant.id} />
        </div>
      )}

      {/* iter156av: removido — modulos MDF-e/Ponto/Producao ficam APENAS no
          Tipo de Negocio (Business Type). Ver secao "Modulos Adicionais" em
          SuperAdmin > Tipos de Negocio > Editar. */}

      <div className="flex gap-3">
        <button type="submit" disabled={submitting} data-testid="tenant-submit"
          className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-violet-500 hover:to-indigo-500 disabled:opacity-50 transition-all flex items-center space-x-2"
        >
          <Save size={16} /><span>{submitting ? 'Salvando...' : tenant ? 'Atualizar Empresa' : 'Criar Empresa'}</span>
        </button>
        <button type="button" onClick={onClose} className="bg-[#1e1e3a] text-gray-400 px-6 py-2.5 rounded-xl font-semibold hover:text-white transition-colors">
          Cancelar
        </button>
      </div>

      {/* === CONFIRM DIFF MODAL === */}
      {confirmDiff && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tenant-confirm-modal">
          <div className="bg-[#16162a] border border-violet-500/40 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="bg-gradient-to-r from-amber-600/30 to-rose-600/20 border-b border-amber-500/30 px-6 py-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={20} className="text-amber-300" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-white">Confirmar alteracoes</h3>
                  <p className="text-xs text-amber-200/80 mt-0.5">Voce esta editando o tenant <strong className="text-white">{tenant?.company_name}</strong> (slug: <span className="font-mono text-violet-300">/{tenant?.subdomain}</span>). Confirme cada campo abaixo antes de salvar.</p>
                </div>
              </div>
            </div>
            <div className="overflow-y-auto p-5 space-y-2 flex-1">
              {confirmDiff.diff.map(({ key, label, before, after }) => (
                <div key={key} className="grid grid-cols-12 gap-2 items-center text-xs bg-[#0f0f1e] rounded-lg p-3 border border-[#2a2a4a]">
                  <div className="col-span-3 font-bold text-gray-400 uppercase tracking-wider">{label}</div>
                  <div className="col-span-4 text-rose-300/90 line-through truncate" title={String(before)}>{String(before)}</div>
                  <div className="col-span-1 text-center text-violet-400">&rarr;</div>
                  <div className="col-span-4 text-emerald-300 font-bold truncate" title={String(after)}>{String(after)}</div>
                </div>
              ))}
              <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">
                <strong className="text-amber-300">Atencao:</strong> Mudar nome ou email muda como a empresa aparece e como o admin faz login. O slug <span className="font-mono text-violet-300">/{tenant?.subdomain}</span> e <strong>imutavel</strong> — o roteamento permanece intacto.
              </p>
            </div>
            <div className="border-t border-[#2a2a4a] bg-[#0f0f1e] px-6 py-4 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setConfirmDiff(null)} disabled={submitting} className="bg-[#1e1e3a] text-gray-400 px-5 py-2 rounded-xl font-semibold hover:text-white transition-colors text-sm" data-testid="tenant-confirm-cancel">
                Voltar e revisar
              </button>
              <button type="button" onClick={() => persist(confirmDiff.payload)} disabled={submitting} className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-5 py-2 rounded-xl font-bold hover:from-emerald-500 hover:to-teal-500 transition-all flex items-center gap-2 text-sm disabled:opacity-50" data-testid="tenant-confirm-save">
                <CheckCircle size={16} /> {submitting ? 'Salvando...' : `Confirmar (${confirmDiff.diff.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

/* ============ PLANS MANAGEMENT ============ */
const PlansManagement = () => {
  const [plans, setPlans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchPlans(); }, []);
  const fetchPlans = async () => {
    try {
      const { data } = await axios.get(`${SA}/plans`, { withCredentials: true });
      setPlans(data.data || []);
    } catch { toast.error('Erro ao carregar planos'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (planId) => {
    if (!(await confirmDialog({ title: 'Tem certeza que deseja excluir este plano?', confirmLabel: 'Confirmar', danger: true }))) return;
    try {
      await axios.delete(`${SA}/plans/${planId}`, { withCredentials: true });
      toast.success('Plano excluído!');
      fetchPlans();
    } catch { toast.error('Erro ao excluir plano'); }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Planos</h1>
          <p className="text-gray-400 mt-1 text-sm">Gerencie os planos disponíveis</p>
        </div>
        <button onClick={() => { setEditingPlan(null); setShowForm(!showForm); }} data-testid="new-plan-btn"
          className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-violet-500 hover:to-indigo-500 flex items-center justify-center space-x-2 transition-all shadow-lg shadow-violet-600/20 text-sm"
        >
          <Plus size={18} /><span>Novo Plano</span>
        </button>
      </div>

      {showForm && <PlanForm plan={editingPlan} onClose={() => { setShowForm(false); setEditingPlan(null); fetchPlans(); }} />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((plan, i) => (
          <div key={plan.id} className={`bg-[#16162a] border rounded-2xl p-6 transition-colors relative group ${i === 1 ? 'border-violet-500/50 ring-1 ring-violet-500/20' : 'border-[#2a2a4a] hover:border-violet-500/20'}`} data-testid={`plan-${plan.name.toLowerCase()}`}>
            {i === 1 && <span className="px-3 py-1 bg-violet-600 text-white text-xs font-bold rounded-full mb-3 inline-block">Popular</span>}
            
            {/* Edit/Delete buttons */}
            <div className="absolute top-4 right-4 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => { setEditingPlan(plan); setShowForm(true); }} className="p-2 rounded-lg hover:bg-violet-500/20 text-gray-500 hover:text-violet-400 transition-colors" data-testid="edit-plan-btn">
                <Edit size={15} />
              </button>
              <button onClick={() => handleDelete(plan.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors" data-testid="delete-plan-btn">
                <XCircle size={15} />
              </button>
            </div>

            <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
            {plan.trial_days > 0 && <p className="text-xs text-violet-400 font-semibold mb-1">{plan.trial_days} dias gratis</p>}
            <div className="mb-4">
              <span className="text-3xl font-bold text-white">R$ {plan.price.toFixed(2)}</span>
              <span className="text-gray-500 text-sm">/{plan.billing_cycle}</span>
            </div>
            <div className="space-y-2 mb-4">
              {plan.max_products && <p className="text-xs text-gray-400">Até {plan.max_products} produtos</p>}
              {plan.max_orders && <p className="text-xs text-gray-400">Até {plan.max_orders} pedidos/mês</p>}
              {!plan.max_products && <p className="text-xs text-gray-400">Produtos ilimitados</p>}
            </div>
            <ul className="space-y-2">
              {plan.features?.map((f, j) => (
                <li key={j} className="flex items-start space-x-2 text-sm">
                  <CheckCircle size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {plans.length === 0 && (
        <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-12 text-center">
          <PackageIcon size={48} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Nenhum plano cadastrado</h3>
          <p className="text-gray-500 text-sm">Os planos padrão serão criados automaticamente</p>
        </div>
      )}
    </div>
  );
};

/* ============ PLAN FORM (Create/Edit) ============ */
const PlanForm = ({ plan, onClose }) => {
  const [form, setForm] = useState({
    name: plan?.name || '',
    price: plan?.price || '',
    billing_cycle: plan?.billing_cycle || 'mensal',
    max_products: plan?.max_products || '',
    max_orders: plan?.max_orders || '',
    trial_days: plan?.trial_days || '',
    features: plan?.features?.join('\n') || '',
    // iter156fp: plano -> tipo de negocio / add-on
    kind: plan?.kind || 'plan',
    business_type_id: plan?.business_type_id || '',
    description: plan?.description || '',
    highlight: !!plan?.highlight,
    addon_modules: (plan?.addon_modules || []).join(', '),
    branch_price: plan?.branch_price ?? '',
    sort_order: plan?.sort_order ?? 0,
    active: plan?.active !== false,
  });
  const [bts, setBts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/public/business-types`).then(({ data }) => setBts(data.data || [])).catch(() => setBts([]));
  }, []);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        price: parseFloat(form.price),
        billing_cycle: form.billing_cycle,
        max_products: form.max_products ? parseInt(form.max_products) : null,
        max_orders: form.max_orders ? parseInt(form.max_orders) : null,
        trial_days: form.trial_days ? parseInt(form.trial_days) : 0,
        features: form.features.split('\n').map(f => f.trim()).filter(Boolean),
        kind: form.kind,
        business_type_id: form.kind === 'plan' ? (form.business_type_id || null) : null,
        description: form.description,
        highlight: !!form.highlight,
        addon_modules: form.kind === 'addon' ? form.addon_modules.split(',').map(x => x.trim()).filter(Boolean) : [],
        branch_price: form.branch_price === '' ? null : parseFloat(form.branch_price),
        sort_order: parseInt(form.sort_order, 10) || 0,
        active: !!form.active,
      };
      if (plan) {
        await axios.put(`${SA}/plans/${plan.id}`, payload, { withCredentials: true });
        toast.success('Plano atualizado!');
      } else {
        await axios.post(`${SA}/plans`, payload, { withCredentials: true });
        toast.success('Plano criado!');
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar plano');
    } finally { setSubmitting(false); }
  };

  const inputClass = "w-full px-4 py-2.5 bg-[#1e1e3a] border border-[#2a2a4a] rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500";

  return (
    <form onSubmit={handleSubmit} className="bg-[#16162a] border border-violet-500/30 rounded-2xl p-6 mb-6" data-testid="plan-form">
      <h2 className="text-xl font-bold text-white mb-5">{plan ? 'Editar Plano' : 'Criar Novo Plano'}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Nome do Plano *</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Premium" className={inputClass} required data-testid="plan-name" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Preço (R$) *</label>
          <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="197.00" className={inputClass} required data-testid="plan-price" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Ciclo de Cobrança</label>
          <select value={form.billing_cycle} onChange={(e) => setForm({ ...form, billing_cycle: e.target.value })} className={inputClass} data-testid="plan-cycle">
            <option value="mensal">Mensal</option>
            <option value="trimestral">Trimestral</option>
            <option value="semestral">Semestral</option>
            <option value="anual">Anual</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Max Produtos</label>
          <input type="number" value={form.max_products} onChange={(e) => setForm({ ...form, max_products: e.target.value })} placeholder="Vazio = ilimitado" className={inputClass} data-testid="plan-max-products" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Max Pedidos/Mes</label>
          <input type="number" value={form.max_orders} onChange={(e) => setForm({ ...form, max_orders: e.target.value })} placeholder="Vazio = ilimitado" className={inputClass} data-testid="plan-max-orders" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Dias de Teste Gratis</label>
          <input type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} placeholder="0 = sem teste" className={inputClass} data-testid="plan-trial-days" />
        </div>
      </div>
      <div className="mb-5">
        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Recursos (um por linha)</label>
        <textarea value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder={"Catálogo digital\nGestão de pedidos\nSuporte por e-mail"} rows={4} className={`${inputClass} resize-none`} data-testid="plan-features" />
      </div>
      <div className="rounded-xl border border-violet-500/20 bg-violet-950/20 p-4 space-y-3" data-testid="plan-landing-block">
        <p className="text-xs font-bold text-violet-200 uppercase tracking-wider">Landing page & permissoes</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Tipo</label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={inputClass} data-testid="plan-kind">
              <option value="plan">Plano principal</option>
              <option value="addon">Add-on (modulo a parte)</option>
            </select>
          </div>
          {form.kind === 'plan' ? (
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Tipo de negocio (permissoes)</label>
              <select value={form.business_type_id} onChange={(e) => setForm({ ...form, business_type_id: e.target.value })} className={inputClass} data-testid="plan-business-type">
                <option value="">— nenhum —</option>
                {bts.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <p className="text-[10px] text-gray-500 mt-1">Quem contratar este plano assume automaticamente os modulos deste tipo (troca so via Super Admin).</p>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Modulos que o add-on libera</label>
              <input value={form.addon_modules} onChange={(e) => setForm({ ...form, addon_modules: e.target.value })} placeholder="pharmacy, production-orders, ponto, service-orders, metas, cashback" className={inputClass} data-testid="plan-addon-modules" />
              <p className="text-[10px] text-gray-500 mt-1">Chaves separadas por virgula (pharmacy · production-orders · ponto · ponto-report · service-orders · metas · cashback · mdfe · manifesto · purchases · invoices).</p>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Descricao curta (landing)</label>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} data-testid="plan-description" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Filial adicional (R$)</label>
            <input type="number" step="0.01" value={form.branch_price} onChange={(e) => setForm({ ...form, branch_price: e.target.value })} placeholder="129" className={inputClass} data-testid="plan-branch-price" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Ordem</label>
            <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className={inputClass} data-testid="plan-sort-order" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300 mt-6 cursor-pointer"><input type="checkbox" checked={form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.checked })} className="accent-violet-600" data-testid="plan-highlight" /> Destaque</label>
          <label className="flex items-center gap-2 text-sm text-gray-300 mt-6 cursor-pointer"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-violet-600" data-testid="plan-active" /> Visivel na landing</label>
        </div>
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={submitting} data-testid="plan-submit"
          className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-violet-500 hover:to-indigo-500 disabled:opacity-50 transition-all"
        >
          {submitting ? 'Salvando...' : plan ? 'Atualizar Plano' : 'Criar Plano'}
        </button>
        <button type="button" onClick={onClose} className="bg-[#1e1e3a] text-gray-400 px-6 py-2.5 rounded-xl font-semibold hover:text-white transition-colors">
          Cancelar
        </button>
      </div>
    </form>
  );
};

/* ============ BILLING SUMMARY (mensalidade por empresa) ============ */
const fmtMoneySA = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BillingSummary = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${SA}/billing/summary`, { withCredentials: true });
        setRows(data.data || []); setTotals(data.totals || {}); setCounts(data.counts || {});
      } catch { toast.error('Erro ao carregar mensalidades'); }
      finally { setLoading(false); }
    })();
  }, []);
  const filtered = rows.filter(r => {
    if (filter === 'active' && !r.is_active) return false;
    if (filter === 'inactive' && r.is_active) return false;
    if (filter === 'blocked' && !r.is_blocked) return false;
    if (q) {
      const s = q.toLowerCase();
      return (r.company_name || '').toLowerCase().includes(s) || (r.owner_name || '').toLowerCase().includes(s) || (r.subdomain || '').toLowerCase().includes(s);
    }
    return true;
  });
  const sum = filtered.reduce((a, r) => a + (Number(r.monthly_value) || 0), 0);
  const chips = [['all', 'Todas'], ['active', 'Ativas'], ['inactive', 'Inativas'], ['blocked', 'Bloqueadas']];
  const cardCls = (k) => `rounded-xl border p-4 text-left transition-colors ${filter === k ? 'border-violet-500 bg-violet-500/10' : 'border-[#2a2a4a] bg-[#16162a] hover:border-violet-500/40'}`;
  return (
    <div className="mb-8" data-testid="billing-summary">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-white">Mensalidades por Empresa</h2>
          <p className="text-gray-400 text-xs mt-0.5">Valor mensal de cada empresa (matriz + filiais − desconto), dia de vencimento e representante</p>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar empresa ou representante..." className="px-3 py-2 bg-[#1e1e3a] border border-[#2a2a4a] rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 w-64" data-testid="billing-search" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {chips.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} className={cardCls(k)} data-testid={`billing-filter-${k}`}>
            <p className={`text-[10px] font-bold uppercase tracking-wider ${k === 'blocked' ? 'text-rose-400' : k === 'inactive' ? 'text-gray-400' : k === 'active' ? 'text-emerald-400' : 'text-violet-300'}`}>{l} · {counts[k] ?? 0}</p>
            <p className="text-xl font-black text-white mt-1" data-testid={`billing-total-${k}`}>{fmtMoneySA(totals[k])}</p>
            <p className="text-[10px] text-gray-500">por mes</p>
          </button>
        ))}
      </div>
      {loading ? <Loader /> : (
        <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="billing-table">
              <thead className="bg-[#1e1e3a] text-gray-400 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Empresa</th>
                  <th className="text-left px-4 py-3">Representante</th>
                  <th className="text-center px-4 py-3">Vencimento</th>
                  <th className="text-right px-4 py-3">Valor Mensal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2a2a4a]">
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500 text-sm">Nenhuma empresa neste filtro</td></tr>
                )}
                {filtered.map(r => (
                  <tr key={r.id} className={`hover:bg-[#1e1e3a]/60 ${!r.is_active ? 'opacity-60' : ''}`} data-testid={`billing-row-${r.subdomain}`}>
                    <td className="px-4 py-2.5">
                      <p className="font-semibold text-white flex items-center gap-2 flex-wrap">
                        {r.company_name}
                        {r.is_blocked && <span className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 text-[9px] font-black uppercase">Bloqueada</span>}
                        {!r.is_active && <span className="px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-300 text-[9px] font-black uppercase">Inativa</span>}
                      </p>
                      <p className="text-[11px] text-gray-500">{r.subdomain}{r.plan_name ? ` · ${r.plan_name}` : ''}</p>
                    </td>
                    <td className="px-4 py-2.5 text-gray-300">{r.billing_contact_name || r.owner_name || '—'}{r.owner_phone ? <span className="block text-[11px] text-gray-500">{r.owner_phone}</span> : null}</td>
                    <td className="px-4 py-2.5 text-center text-gray-300 font-mono">dia {String(r.billing_day).padStart(2, '0')}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-white font-mono" data-testid={`billing-value-${r.subdomain}`}>{fmtMoneySA(r.monthly_value)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#1e1e3a]">
                <tr>
                  <td className="px-4 py-3 font-bold text-white" colSpan={3}>Total ({filtered.length} empresa{filtered.length === 1 ? '' : 's'})</td>
                  <td className="px-4 py-3 text-right font-black text-emerald-400 font-mono text-base" data-testid="billing-filtered-total">{fmtMoneySA(sum)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============ PAYMENTS MANAGEMENT ============ */
const PaymentsManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchPayments(); }, []);
  const fetchPayments = async () => {
    try {
      const { data } = await axios.get(`${SA}/payments`, { withCredentials: true });
      setPayments(data.data || []);
    } catch { toast.error('Erro ao carregar pagamentos'); }
    finally { setLoading(false); }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Financeiro</h1>
        <p className="text-gray-400 mt-1">Mensalidades por empresa e histórico de pagamentos</p>
      </div>

      <BillingSummary />

      <h2 className="text-lg font-bold text-white mb-3">Histórico de pagamentos</h2>
      {payments.length === 0 ? (
        <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-12 text-center">
          <CreditCard size={48} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Nenhum pagamento registrado</h3>
          <p className="text-gray-500 text-sm">Os pagamentos aparecerão aqui quando forem processados</p>
        </div>
      ) : (
        <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#1e1e3a] border-b border-[#2a2a4a]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Data</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Descrição</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Método</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Valor</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a2a4a]">
              {payments.map((p) => (
                <tr key={p.id} className="hover:bg-[#1e1e3a] transition-colors">
                  <td className="px-6 py-4 text-sm text-gray-300">{new Date(p.created_at).toLocaleDateString('pt-BR')}</td>
                  <td className="px-6 py-4 text-sm text-white">{p.description}</td>
                  <td className="px-6 py-4 text-sm text-gray-400">{p.payment_method}</td>
                  <td className="px-6 py-4 text-sm text-white text-right font-semibold">R$ {p.amount.toFixed(2)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${p.status === 'paid' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>
                      {p.status === 'paid' ? 'Pago' : p.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ============ BUSINESS TYPES MANAGEMENT ============ */
const FIELD_LABELS = [
  { key: 'show_brand', label: 'Marca' },
  { key: 'show_category', label: 'Categoria' },
  { key: 'show_grade', label: 'Grade' },
  { key: 'show_kit_qty', label: 'Qtd no Kit' },
  { key: 'show_unit_price', label: 'Valor' },
  { key: 'show_kit_price', label: 'Valor por item (unitario)' },
  { key: 'show_reference', label: 'Referencia' },
  { key: 'show_weight', label: 'Peso' },
  { key: 'show_stock', label: 'Estoque' },
  { key: 'show_sku', label: 'SKU' },
  { key: 'show_description', label: 'Descricao' }
];

const ERP_MODULE_LABELS = [
  { key: 'dashboard', label: 'Dashboard', group: 'Base' },
  { key: 'companies', label: 'Empresas', group: 'Base' },
  { key: 'company_settings', label: 'Config Empresa', group: 'Base' },
  { key: 'users', label: 'Usuarios', group: 'Base' },
  { key: 'access_profiles', label: 'Perfis de Acesso', group: 'Base' },
  { key: 'plans', label: 'Meu Plano', group: 'Base' },
  { key: 'pdv', label: 'PDV', group: 'Vendas' },
  { key: 'quotes', label: 'Orcamentos', group: 'Vendas' },
  { key: 'sales', label: 'Vendas', group: 'Vendas' },
  { key: 'sales_orders', label: 'Pedidos de Venda', group: 'Vendas' },
  { key: 'cash', label: 'Caixa', group: 'Vendas' },
  { key: 'sales_report', label: 'Relatorio Vendas', group: 'Vendas' },
  { key: 'metas', label: 'Metas (Targets)', group: 'Vendas' },
  { key: 'stock', label: 'Estoque', group: 'Vendas' },
  { key: 'stock_movements', label: 'Movimentacao Estoque', group: 'Vendas' },
  { key: 'stock_transfers', label: 'Transferencia Estoque', group: 'Vendas' },
  { key: 'products_update_xlsx', label: 'Atualizar Produto via Planilha', group: 'Vendas' },
  { key: 'products_share', label: 'Compartilhar Produto entre Empresas', group: 'Vendas' },
  { key: 'price_tables', label: 'Tabela de Precos', group: 'Vendas' },
  { key: 'products', label: 'Produtos', group: 'Catalogo' },
  { key: 'categories', label: 'Categorias', group: 'Catalogo' },
  { key: 'orders', label: 'Pedidos', group: 'Catalogo' },
  { key: 'catalog_settings', label: 'Config Catalogo', group: 'Catalogo' },
  { key: 'clients', label: 'Clientes', group: 'Cadastros' },
  { key: 'suppliers', label: 'Fornecedores', group: 'Cadastros' },
  { key: 'employees', label: 'Funcionarios', group: 'Cadastros' },
  { key: 'financial', label: 'Financeiro', group: 'Financeiro' },
  { key: 'chart_accounts', label: 'Plano de Contas', group: 'Financeiro' },
  { key: 'bank_accounts', label: 'Conta Banco', group: 'Financeiro' },
  { key: 'payment_methods', label: 'Formas Pgto', group: 'Financeiro' },
  { key: 'closing', label: 'Fechamento', group: 'Financeiro' },
  { key: 'receipts', label: 'Recibos', group: 'Financeiro' },
  { key: 'integrations', label: 'Integracoes (CRM/WhatsApp)', group: 'Administracao' },
  { key: 'natureza_operacao', label: 'Config. de NF (Natureza de Operacao)', group: 'Fiscal (Opt-in)' },
  { key: 'invoices', label: 'NF (Emissao NF-e/NFC-e)', group: 'Fiscal (Opt-in)' },
  { key: 'manifesto', label: 'NF de Entrada (Manifesto)', group: 'Fiscal (Opt-in)' },
  { key: 'cashback', label: 'Cashback (Saldo / Credito do Cliente)', group: 'Fiscal (Opt-in)' },
  // iter156el: modulo Compras com grupo proprio (libera o menu "Compras" no ERP e nos Perfis de Acesso)
  { key: 'purchases', label: 'Compras (Pedidos de Compra / Entrada de NF)', group: 'Compras (Opt-in)' },
  { key: 'purchase_requests', label: 'Solicitacoes de Compra', group: 'Compras (Opt-in)' },
  { key: 'pharmacy', label: 'Farmacia (SNGPC/CMED/Receituario)', group: 'Farmacia' },
  // iter156aq: modulos toggle-avel via SuperAdmin — quando ligados no BT,
  // TODOS os tenants desse BT herdam. Podem tambem ser ligados individualmente
  // na tela de editar Tenant.
  { key: 'mdfe', label: 'MDF-e (Manifesto Eletronico de Documentos Fiscais)', group: 'Modulos Adicionais' },
  { key: 'ponto', label: 'Ponto Eletronico (Jornada de Trabalho)', group: 'Modulos Adicionais' },
  { key: 'production', label: 'Producao (BOM + Ordens de Producao)', group: 'Modulos Adicionais' },
  // iter156dz: Ordens de Servico / Locacao — habilita checkbox "E servico" no produto + menu OS
  { key: 'os', label: 'Ordem de Servico / Locacao', group: 'Modulos Adicionais' },
];

const BusinessTypesManagement = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState('all');

  useEffect(() => { fetchTypes(); }, []);
  const fetchTypes = async () => {
    try {
      const { data } = await axios.get(`${SA}/business-types`, { withCredentials: true });
      setTypes(data.data || []);
    } catch { toast.error('Erro ao carregar tipos'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Excluir este tipo de negocio?', confirmLabel: 'Confirmar', danger: true }))) return;
    try {
      await axios.delete(`${SA}/business-types/${id}`, { withCredentials: true });
      toast.success('Excluido!');
      fetchTypes();
    } catch { toast.error('Erro ao excluir'); }
  };

  if (loading) return <Loader />;

  const catalogTypes = types.filter(t => (t.category || 'catalogo') === 'catalogo');
  const erpTypes = types.filter(t => t.category === 'erp');
  const filtered = filterCat === 'all' ? types : filterCat === 'catalogo' ? catalogTypes : erpTypes;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Tipos de Negocio</h1>
          <p className="text-gray-400 mt-1 text-sm">{catalogTypes.length} catalogo • {erpTypes.length} ERP</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(!showForm); }} data-testid="new-bt-btn"
          className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-violet-500 hover:to-indigo-500 flex items-center justify-center space-x-2 transition-all shadow-lg shadow-violet-600/20 text-sm"
        >
          <Plus size={18} /><span>Novo Tipo</span>
        </button>
      </div>

      {/* Category filter tabs */}
      <div className="flex gap-2 mb-5">
        {[{ key: 'all', label: 'Todos' }, { key: 'catalogo', label: 'Catalogo' }, { key: 'erp', label: 'ERP / Gestao' }].map(tab => (
          <button key={tab.key} onClick={() => setFilterCat(tab.key)} data-testid={`filter-${tab.key}`}
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${filterCat === tab.key ? 'bg-violet-600/30 text-violet-300 border border-violet-500/30' : 'bg-[#1e1e3a] text-gray-500 border border-[#2a2a4a] hover:text-gray-300'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {showForm && <BusinessTypeForm bt={editing} onClose={() => { setShowForm(false); setEditing(null); fetchTypes(); }} />}

      <div className="space-y-4">
        {filtered.map(bt => {
          const isErp = bt.category === 'erp';
          return (
            <div key={bt.id} className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-5 hover:border-violet-500/20 transition-colors" data-testid={`bt-${bt.slug}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center space-x-2">
                    <Briefcase size={18} className={isErp ? 'text-teal-400' : 'text-violet-400'} />
                    <h3 className="text-lg font-bold text-white">{bt.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isErp ? 'bg-teal-500/20 text-teal-400' : 'bg-violet-500/20 text-violet-400'}`}>
                      {isErp ? 'ERP' : 'Catalogo'}
                    </span>
                  </div>
                  {bt.description && <p className="text-sm text-gray-500 mt-1">{bt.description}</p>}
                  <p className="text-xs text-gray-600 mt-1 font-mono">slug: {bt.slug}</p>
                </div>
                <div className="flex items-center space-x-1">
                  <button onClick={() => { setEditing(bt); setShowForm(true); }} className="p-2 rounded-lg hover:bg-violet-500/20 text-gray-500 hover:text-violet-400 transition-colors" data-testid="edit-bt-btn">
                    <Edit size={15} />
                  </button>
                  <button onClick={() => handleDelete(bt.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors" data-testid="delete-bt-btn">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              {/* Show product fields for catalog types */}
              {!isErp && (
                <div className="flex flex-wrap gap-1.5">
                  {FIELD_LABELS.map(f => {
                    const active = bt.product_fields?.[f.key] !== false;
                    return (
                      <span key={f.key} className={`px-2 py-1 rounded text-xs font-medium ${active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-700/30 text-gray-600 line-through'}`}>
                        {f.label}
                      </span>
                    );
                  })}
                </div>
              )}
              {/* Show ERP modules for ERP types */}
              {isErp && (
                <div className="flex flex-wrap gap-1.5">
                  {bt.pharmacy_module && (
                    <span className="px-2 py-1 rounded text-xs font-bold bg-violet-500/20 text-violet-300 border border-violet-500/40" data-testid={`bt-pharmacy-badge-${bt.id || bt.slug}`}>
                      Farmacia (RX)
                    </span>
                  )}
                  {ERP_MODULE_LABELS.map(m => {
                    const isOptIn = (m.group || '').includes('Opt-in');
                    const active = isOptIn ? bt.erp_modules?.[m.key] === true : bt.erp_modules?.[m.key] !== false;
                    const cls = active
                      ? (isOptIn ? 'bg-amber-500/15 text-amber-400' : 'bg-teal-500/15 text-teal-400')
                      : 'bg-gray-700/30 text-gray-600 line-through';
                    return (
                      <span key={m.key} className={`px-2 py-1 rounded text-xs font-medium ${cls}`}>
                        {m.label}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-12 text-center">
            <Briefcase size={48} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Nenhum tipo nesta categoria</h3>
            <p className="text-gray-500 text-sm">Crie tipos para personalizar os campos por segmento</p>
          </div>
        )}
      </div>
    </div>
  );
};

const BusinessTypeForm = ({ bt, onClose }) => {
  const [form, setForm] = useState({
    name: bt?.name || '',
    slug: bt?.slug || '',
    description: bt?.description || '',
    category: bt?.category || 'catalogo',
    product_fields: bt?.product_fields || {},
    erp_modules: bt?.erp_modules || {},
    pharmacy_module: !!bt?.pharmacy_module,
    mobile_bottom_nav: Array.isArray(bt?.mobile_bottom_nav) ? bt.mobile_bottom_nav : [],  // iter77ct.13
    // iter156bp: dias de historico do Manifesto (0 = todos que SEFAZ tem, ate ~3 anos)
    manifesto_days_back: bt?.manifesto_days_back ?? 30,
    // iter156ef: label customizado do modulo OS por BT
    os_module_label: bt?.os_module_label || '',
  });
  const [submitting, setSubmitting] = useState(false);

  const toggleField = (key) => {
    setForm(prev => ({
      ...prev,
      product_fields: { ...prev.product_fields, [key]: !(prev.product_fields[key] !== false) }
    }));
  };

  const toggleModule = (key, isOptIn = false) => {
    setForm(prev => {
      const cur = prev.erp_modules[key];
      const isActive = isOptIn ? cur === true : cur !== false;
      return {
        ...prev,
        erp_modules: { ...prev.erp_modules, [key]: !isActive },
      };
    });
  };

  const handleNameChange = (name) => {
    setForm(prev => ({
      ...prev,
      name,
      slug: bt ? prev.slug : name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').slice(0, 30)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (bt) {
        await axios.put(`${SA}/business-types/${bt.id}`, form, { withCredentials: true });
        toast.success('Tipo atualizado!');
      } else {
        await axios.post(`${SA}/business-types`, form, { withCredentials: true });
        toast.success('Tipo criado!');
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSubmitting(false); }
  };

  const inputClass = "w-full px-4 py-2.5 bg-[#1e1e3a] border border-[#2a2a4a] rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500";
  const isErp = form.category === 'erp';
  const erpGroups = [...new Set(ERP_MODULE_LABELS.map(m => m.group))];

  return (
    <form onSubmit={handleSubmit} className="bg-[#16162a] border border-violet-500/30 rounded-2xl p-6 mb-6" data-testid="bt-form">
      <h2 className="text-xl font-bold text-white mb-5">{bt ? 'Editar Tipo' : 'Novo Tipo de Negocio'}</h2>

      {/* Category selector */}
      <div className="mb-5">
        <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Categoria do Tipo *</label>
        <div className="flex gap-3">
          <button type="button" onClick={() => setForm(p => ({ ...p, category: 'catalogo' }))} data-testid="bt-cat-catalogo"
            className={`flex-1 p-4 rounded-xl border-2 transition-all text-left ${!isErp ? 'border-violet-500 bg-violet-500/10' : 'border-[#2a2a4a] hover:border-violet-500/30'}`}>
            <p className={`font-bold text-sm ${!isErp ? 'text-violet-300' : 'text-gray-400'}`}>Catalogo</p>
            <p className="text-[10px] text-gray-500 mt-1">Catalogo de produtos com campos personalizaveis</p>
          </button>
          <button type="button" onClick={() => setForm(p => ({ ...p, category: 'erp' }))} data-testid="bt-cat-erp"
            className={`flex-1 p-4 rounded-xl border-2 transition-all text-left ${isErp ? 'border-teal-500 bg-teal-500/10' : 'border-[#2a2a4a] hover:border-teal-500/30'}`}>
            <p className={`font-bold text-sm ${isErp ? 'text-teal-300' : 'text-gray-400'}`}>ERP / Gestao</p>
            <p className="text-[10px] text-gray-500 mt-1">Sistema de gestao com modulos configuraveis</p>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Nome *</label>
          <input type="text" value={form.name} onChange={e => handleNameChange(e.target.value)} placeholder={isErp ? 'Ex: ERP Completo' : 'Ex: Loja de Atacado'} className={inputClass} required data-testid="bt-name" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Slug</label>
          <input type="text" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="slug-unico" className={inputClass} required data-testid="bt-slug" />
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Descricao</label>
        <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descricao do tipo de negocio" className={inputClass} data-testid="bt-description" />
      </div>

      {/* Catalog: Product fields */}
      {!isErp && (
        <div className="mb-5">
          <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Campos do Produto no Catalogo</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FIELD_LABELS.map(f => (
              <label key={f.key} className={`flex items-center space-x-2 p-2.5 border rounded-xl cursor-pointer transition-colors ${form.product_fields[f.key] !== false ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-[#2a2a4a] bg-[#1e1e3a]'}`}>
                <input type="checkbox" checked={form.product_fields[f.key] !== false} onChange={() => toggleField(f.key)} className="sr-only" />
                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${form.product_fields[f.key] !== false ? 'bg-emerald-500 border-emerald-500' : 'border-gray-600'}`}>
                  {form.product_fields[f.key] !== false && <CheckCircle size={10} className="text-white" />}
                </div>
                <span className={`text-xs font-medium ${form.product_fields[f.key] !== false ? 'text-emerald-300' : 'text-gray-500'}`}>{f.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* ERP: Module toggles */}
      {isErp && (
        <div className="mb-5">
          <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">Modulos do ERP</label>
          <p className="text-[10px] text-gray-600 mb-3">Selecione quais modulos o cliente tera acesso. Modulos do grupo &quot;Fiscal&quot; sao opt-in — precisam ser ligados explicitamente.</p>
          {erpGroups.map(group => {
            // iter156aq: 'Modulos Adicionais' tambem eh opt-in (default false).
            const isFiscalOptIn = (group || '').includes('Opt-in') || (group === 'Modulos Adicionais');
            return (
            <div key={group} className="mb-3">
              <p className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${isFiscalOptIn ? 'text-amber-400' : 'text-teal-400'}`}>{group}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ERP_MODULE_LABELS.filter(m => m.group === group).map(m => {
                  // Opt-in: must be explicit True. Opt-out: True unless explicit False
                  const isActive = isFiscalOptIn ? form.erp_modules[m.key] === true : form.erp_modules[m.key] !== false;
                  const accent = isFiscalOptIn ? 'amber' : 'teal';
                  return (
                  <label key={m.key} className={`flex items-center space-x-2 p-2.5 border rounded-xl cursor-pointer transition-colors ${isActive ? `border-${accent}-500/30 bg-${accent}-500/5` : 'border-[#2a2a4a] bg-[#1e1e3a]'}`}>
                    <input type="checkbox" checked={isActive} onChange={() => toggleModule(m.key, isFiscalOptIn)} className="sr-only" />
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isActive ? `bg-${accent}-500 border-${accent}-500` : 'border-gray-600'}`}>
                      {isActive && <CheckCircle size={10} className="text-white" />}
                    </div>
                    <span className={`text-xs font-medium ${isActive ? `text-${accent}-300` : 'text-gray-500'}`}>{m.label}</span>
                  </label>
                  );
                })}
              </div>
              {/* iter156bp: quando Manifesto esta ligado, mostra o input de dias de historico */}
              {group === 'Fiscal (Opt-in)' && form.erp_modules['manifesto'] === true && (
                <div className="mt-2 p-3 rounded-xl border border-amber-500/40 bg-amber-500/10 flex items-center gap-3">
                  <span className="text-[11px] font-black text-amber-300 uppercase tracking-wider whitespace-nowrap">Historico do Manifesto</span>
                  <input
                    type="number"
                    min="0"
                    max="1095"
                    step="1"
                    value={form.manifesto_days_back}
                    onChange={(e) => setForm(prev => ({ ...prev, manifesto_days_back: Math.max(0, Math.min(1095, parseInt(e.target.value || '0'))) }))}
                    className="w-20 px-2 py-1 text-sm bg-[#1e1e3a] border border-amber-500/40 rounded-lg text-white font-mono text-center focus:border-amber-400 focus:outline-none"
                    data-testid="bt-manifesto-days-back"
                  />
                  <span className="text-[11px] text-amber-200/80">dias (0 = todos; recomendado 30-90)</span>
                </div>
              )}
            </div>
            );
          })}

          {/* Pharmacy module master toggle */}
          <div className="mt-4 p-3 rounded-xl border-2 border-violet-500/30 bg-violet-500/5">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5 text-violet-300">Modulo Farmacia (Pharmacy)</p>
            <p className="text-[10px] text-gray-400 mb-2">Quando ligado, habilita PDV Farmacia (tarjas, controlados, CMED, alergias), tab Farmacia em Produtos/Clientes, e os menus SNGPC/CMED/Receituario. Independente da chave <code className="text-violet-300">pharmacy</code> em Modulos.</p>
            <label className={`flex items-center space-x-2 p-2.5 border rounded-xl cursor-pointer transition-colors ${form.pharmacy_module ? 'border-violet-500/50 bg-violet-500/10' : 'border-[#2a2a4a] bg-[#1e1e3a]'}`} data-testid="bt-pharmacy-module-label">
              <input type="checkbox" checked={!!form.pharmacy_module} onChange={() => setForm(p => ({ ...p, pharmacy_module: !p.pharmacy_module }))} className="sr-only" data-testid="bt-pharmacy-module" />
              <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${form.pharmacy_module ? 'bg-violet-500 border-violet-500' : 'border-gray-600'}`}>
                {form.pharmacy_module && <CheckCircle size={10} className="text-white" />}
              </div>
              <span className={`text-xs font-medium ${form.pharmacy_module ? 'text-violet-200' : 'text-gray-500'}`}>Habilitar regras de farmacia (PDV gating + tabs RX em cadastros)</span>
            </label>
          </div>

          {/* iter156ef: Nome customizado do modulo OS — aparece so quando "Ordem de Servico / Locacao" esta ligado */}
          {form.erp_modules?.os === true && (
            <div className="mt-4 p-3 rounded-xl border-2 border-amber-500/40 bg-amber-500/5" data-testid="bt-os-label-block">
              <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5 text-amber-300">Nome do Modulo Ordem de Servico</p>
              <p className="text-[10px] text-gray-400 mb-2">Como o menu aparece para o cliente. Ex: &quot;Ordem de Locacao&quot;, &quot;Ordem de Manutencao&quot;, &quot;Chamado Tecnico&quot;. Deixe vazio para usar o padrao &quot;Ordem de Servico&quot;. Este nome pode ser sobrescrito por tenant na tela de Empresas.</p>
              <input
                type="text"
                value={form.os_module_label}
                onChange={e => setForm(p => ({ ...p, os_module_label: e.target.value }))}
                placeholder="Padrao: Ordem de Servico"
                className={inputClass}
                data-testid="bt-os-module-label"
              />
            </div>
          )}
        </div>
      )}

      {/* iter77ct.13: Mobile Bottom Nav — escolher ate 4 modulos que aparecem na barra inferior mobile */}
      {isErp && (
        <div className="mb-5 p-4 rounded-2xl border-2 border-cyan-500/30 bg-gradient-to-br from-cyan-500/5 to-teal-500/5" data-testid="bt-mobile-bottom-nav">
          <div className="mb-3">
            <label className="block text-xs font-semibold text-cyan-300 mb-1 uppercase tracking-wider flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></span>
              Barra Inferior Mobile
            </label>
            <p className="text-[10px] text-gray-400">Escolha ate 4 modulos que aparecem como atalhos na barra inferior do celular. O quinto botao (Menu ☰) sempre existe e abre todo o resto.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[0, 1, 2, 3].map(slot => {
              const selectedKey = form.mobile_bottom_nav[slot] || '';
              // apenas modulos habilitados no BT podem ser escolhidos
              const enabledMods = ERP_MODULE_LABELS.filter(m => {
                const val = form.erp_modules[m.key];
                // fiscal modules sao opt-in (default false); demais sao opt-out (default true)
                const optIn = (m.group || '').includes('Opt-in');
                return optIn ? val === true : val !== false;
              });
              return (
                <div key={slot} className="bg-[#1e1e3a] border border-cyan-500/20 rounded-xl p-2.5">
                  <p className="text-[10px] font-bold text-cyan-300 uppercase mb-1.5">Slot {slot + 1}{slot < 2 ? ' (esquerda)' : ' (direita)'}</p>
                  <select value={selectedKey} onChange={e => {
                    const val = e.target.value;
                    setForm(prev => {
                      const next = [...(prev.mobile_bottom_nav || [])];
                      while (next.length < 4) next.push('');
                      next[slot] = val;
                      // remove duplicatas do mesmo item em outros slots
                      for (let i = 0; i < 4; i++) if (i !== slot && next[i] === val && val) next[i] = '';
                      return { ...prev, mobile_bottom_nav: next.filter((v, i) => i < 4) };
                    });
                  }} className="w-full px-2 py-1.5 bg-[#0f0f24] border border-cyan-500/20 rounded text-xs text-white focus:outline-none focus:border-cyan-500" data-testid={`bt-mobile-slot-${slot}`}>
                    <option value="">— vazio —</option>
                    {enabledMods.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
          <div className="bg-[#0f0f24] rounded-xl px-3 py-2 border border-cyan-500/10">
            <p className="text-[10px] text-cyan-200/70 mb-1">Preview da barra:</p>
            <div className="flex items-center justify-around bg-gradient-to-r from-[#0d0d1f] to-[#111133] rounded-lg py-2 border border-cyan-500/20">
              {[0, 1].map(i => {
                const k = form.mobile_bottom_nav[i];
                const m = ERP_MODULE_LABELS.find(x => x.key === k);
                return <div key={i} className={`flex-1 text-center text-[10px] font-bold ${m ? 'text-cyan-300' : 'text-gray-600'}`}>{m ? m.label : '—'}</div>;
              })}
              <div className="flex-1 text-center"><span className="inline-block w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-violet-600"></span></div>
              {[2, 3].map(i => {
                const k = form.mobile_bottom_nav[i];
                const m = ERP_MODULE_LABELS.find(x => x.key === k);
                return <div key={i} className={`flex-1 text-center text-[10px] font-bold ${m ? 'text-cyan-300' : 'text-gray-600'}`}>{m ? m.label : '—'}</div>;
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button type="submit" disabled={submitting} data-testid="bt-submit"
          className={`px-6 py-2.5 rounded-xl font-semibold disabled:opacity-50 transition-all flex items-center space-x-2 text-white ${isErp ? 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500' : 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500'}`}
        >
          <Save size={16} /><span>{submitting ? 'Salvando...' : bt ? 'Atualizar' : 'Criar Tipo'}</span>
        </button>
        <button type="button" onClick={onClose} className="bg-[#1e1e3a] text-gray-400 px-6 py-2.5 rounded-xl font-semibold hover:text-white transition-colors">
          Cancelar
        </button>
      </div>
    </form>
  );
};


/* ============ SUPERADMIN USERS MANAGEMENT ============ */
const SAUsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchUsers(); }, []);
  const fetchUsers = async () => {
    try { const { data } = await axios.get(`${SA}/users`, { withCredentials: true }); setUsers(data.data || []); }
    catch { toast.error('Erro ao carregar usuarios'); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setForm({ name: '', email: '', password: '' }); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email };
        if (form.password) payload.password = form.password;
        await axios.put(`${SA}/users/${editing.id}`, payload, { withCredentials: true });
        toast.success('Usuario atualizado!');
      } else {
        if (!form.password || form.password.length < 6) { toast.error('Senha minima 6 caracteres'); setSubmitting(false); return; }
        await axios.post(`${SA}/users`, form, { withCredentials: true });
        toast.success('Usuario criado!');
      }
      setShowForm(false); resetForm(); fetchUsers();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join('; ') : (err.message || 'Erro'));
      toast.error(`Erro ao salvar usuario: ${msg}`);
    }
    finally { setSubmitting(false); }
  };

  const toggleActive = async (user) => {
    try {
      await axios.put(`${SA}/users/${user.id}`, { active: !(user.active !== false) }, { withCredentials: true });
      toast.success(user.active !== false ? 'Desativado' : 'Ativado');
      fetchUsers();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join('; ') : (err.message || 'Erro')));
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Desativar este usuario?', confirmLabel: 'Confirmar', danger: true }))) return;
    try { await axios.delete(`${SA}/users/${id}`, { withCredentials: true }); toast.success('Desativado'); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  if (loading) return <Loader />;

  const inputClass = "w-full px-4 py-2.5 bg-[#1e1e3a] border border-[#2a2a4a] rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Usuarios Super Admin</h1>
          <p className="text-gray-400 mt-1 text-sm">{users.length} {users.length === 1 ? 'usuario' : 'usuarios'}</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} data-testid="new-sa-user-btn"
          className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-violet-500 hover:to-indigo-500 flex items-center justify-center space-x-2 transition-all shadow-lg shadow-violet-600/20 text-sm"
        >
          <Plus size={18} /><span>Novo Usuario</span>
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-[#16162a] border border-violet-500/30 rounded-2xl p-6 mb-6" data-testid="sa-user-form">
          <h2 className="text-xl font-bold text-white mb-5">{editing ? 'Editar Usuario' : 'Novo Usuario Super Admin'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Nome *</label>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nome completo" className={inputClass} required data-testid="sa-user-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">E-mail *</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@empresa.com" className={inputClass} required data-testid="sa-user-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">{editing ? 'Nova Senha (opcional)' : 'Senha *'}</label>
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={editing ? 'Deixe vazio para manter' : 'Min. 6 caracteres'} className={inputClass} required={!editing} data-testid="sa-user-password" />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={submitting} data-testid="sa-user-submit"
              className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-violet-500 hover:to-indigo-500 disabled:opacity-50 transition-all flex items-center space-x-2"
            >
              <Save size={16} /><span>{submitting ? 'Salvando...' : editing ? 'Atualizar' : 'Criar Usuario'}</span>
            </button>
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="bg-[#1e1e3a] text-gray-400 px-6 py-2.5 rounded-xl font-semibold hover:text-white transition-colors">
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {users.map(u => (
          <div key={u.id} className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-5 hover:border-violet-500/20 transition-colors" data-testid={`sa-user-${u.email}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-violet-400">{u.name?.charAt(0) || '?'}</span>
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">{u.name}</h3>
                  <p className="text-sm text-gray-500">{u.email}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-violet-500/15 text-violet-400">Super Admin</span>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${u.active !== false ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                      {u.active !== false ? 'Ativo' : 'Inativo'}
                    </span>
                    {u.created_at && <span className="text-xs text-gray-600">Criado: {new Date(u.created_at).toLocaleDateString('pt-BR')}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button onClick={() => { setEditing(u); setForm({ name: u.name, email: u.email, password: '' }); setShowForm(true); }} className="p-2 rounded-lg hover:bg-violet-500/20 text-gray-500 hover:text-violet-400 transition-colors" title="Editar" data-testid="edit-sa-user-btn">
                  <Edit size={16} />
                </button>
                <button onClick={() => toggleActive(u)} className="p-2 rounded-lg hover:bg-amber-500/10 text-gray-500 hover:text-amber-400 transition-colors" title={u.active !== false ? 'Desativar' : 'Ativar'}>
                  {u.active !== false ? <XCircle size={16} /> : <CheckCircle size={16} />}
                </button>
                <button onClick={() => handleDelete(u.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors" title="Excluir" data-testid="delete-sa-user-btn">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}

        {users.length === 0 && (
          <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-12 text-center">
            <Users size={48} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Nenhum usuario</h3>
            <p className="text-gray-500 text-sm">Crie usuarios para acessar o painel de controle</p>
          </div>
        )}
      </div>
    </div>
  );
};


/* ============ SHARED ============ */
const Loader = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-10 w-10 border-2 border-violet-600 border-t-transparent"></div>
  </div>
);


/* ============ DB HEALTH ============ */
const DBHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all|risk|empty

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${SA}/db/index-health`, { withCredentials: true });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Loader />;
  if (!data) return null;

  const cols = data.collections || [];
  const filtered = cols.filter(c => {
    if (filter === 'risk') return !!c.risk;
    if (filter === 'empty') return (c.doc_count || 0) === 0;
    if (filter === 'mt') return c.is_multi_tenant;
    return true;
  }).sort((a, b) => {
    // risk first, then by doc count desc
    if (!!a.risk !== !!b.risk) return a.risk ? -1 : 1;
    return (b.doc_count || 0) - (a.doc_count || 0);
  });
  const s = data.summary || {};

  return (
    <div data-testid="db-health-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-white flex items-center gap-2"><Database className="text-violet-400" /> Saude do Banco</h2>
          <p className="text-xs text-gray-500">Estado dos indices MongoDB. Quando uma collection ficar &gt;10k docs SEM indice de tenant_id, ela aparece como HIGH RISK aqui.</p>
        </div>
        <button onClick={load} className="px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="db-health-refresh">
          <RefreshCw size={12} /> Atualizar
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-4">
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Collections</div>
          <div className="text-3xl font-black text-white mt-1" data-testid="db-summary-collections">{s.total_collections}</div>
        </div>
        <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-4">
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Total de Docs</div>
          <div className="text-3xl font-black text-white mt-1" data-testid="db-summary-docs">{(s.total_docs || 0).toLocaleString('pt-BR')}</div>
        </div>
        <div className={`border rounded-2xl p-4 ${(s.high_risk?.length || 0) > 0 ? 'bg-rose-950/40 border-rose-500/40' : 'bg-emerald-950/30 border-emerald-500/30'}`}>
          <div className={`text-[10px] font-bold uppercase tracking-wider ${(s.high_risk?.length || 0) > 0 ? 'text-rose-300' : 'text-emerald-300'}`}>High Risk</div>
          <div className={`text-3xl font-black mt-1 ${(s.high_risk?.length || 0) > 0 ? 'text-rose-300' : 'text-emerald-300'}`} data-testid="db-summary-high">{(s.high_risk || []).length}</div>
          {(s.high_risk || []).length > 0 && <div className="text-[10px] text-rose-300/80 mt-1 truncate">{s.high_risk.join(', ')}</div>}
        </div>
        <div className={`border rounded-2xl p-4 ${(s.medium_risk?.length || 0) > 0 ? 'bg-amber-950/40 border-amber-500/40' : 'bg-emerald-950/30 border-emerald-500/30'}`}>
          <div className={`text-[10px] font-bold uppercase tracking-wider ${(s.medium_risk?.length || 0) > 0 ? 'text-amber-300' : 'text-emerald-300'}`}>Medium Risk</div>
          <div className={`text-3xl font-black mt-1 ${(s.medium_risk?.length || 0) > 0 ? 'text-amber-300' : 'text-emerald-300'}`} data-testid="db-summary-medium">{(s.medium_risk || []).length}</div>
          {(s.medium_risk || []).length > 0 && <div className="text-[10px] text-amber-300/80 mt-1 truncate">{s.medium_risk.join(', ')}</div>}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-2">
        {[
          { k: 'all', l: 'Todas' },
          { k: 'mt', l: 'Multi-tenant' },
          { k: 'risk', l: 'Em risco' },
          { k: 'empty', l: 'Vazias' },
        ].map(f => (
          <button key={f.k} onClick={() => setFilter(f.k)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${filter === f.k ? 'bg-violet-600 text-white' : 'bg-[#1e1e3a] text-gray-400 hover:text-white'}`} data-testid={`db-filter-${f.k}`}>
            {f.l}
          </button>
        ))}
      </div>

      {/* Tabela */}
      <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1e1e3a] text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Collection</th>
              <th className="px-3 py-2 text-right">Docs</th>
              <th className="px-3 py-2 text-center">Tipo</th>
              <th className="px-3 py-2 text-center">Tenant Idx</th>
              <th className="px-3 py-2 text-center"># Idx</th>
              <th className="px-3 py-2 text-left">Indices</th>
              <th className="px-3 py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2a2a4a]">
            {filtered.map(c => {
              const risk = c.risk || '';
              return (
                <tr key={c.collection} className="hover:bg-[#1e1e3a]/50" data-testid={`db-row-${c.collection}`}>
                  <td className="px-3 py-2 font-mono text-xs text-white">{c.collection}</td>
                  <td className="px-3 py-2 text-right text-xs text-gray-300">{(c.doc_count || 0).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2 text-center text-[10px]">
                    {c.is_multi_tenant ? <span className="bg-violet-500/20 text-violet-300 px-1.5 py-0.5 rounded">MT</span> : <span className="bg-slate-500/20 text-slate-300 px-1.5 py-0.5 rounded">GLOBAL</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {c.is_multi_tenant ? (c.has_tenant_id_index ? <span className="text-emerald-400">✓</span> : <span className="text-rose-400">✗</span>) : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-gray-300">{c.index_count}</td>
                  <td className="px-3 py-2 text-[10px] text-gray-500 max-w-md">
                    <div className="flex flex-wrap gap-1">
                      {(c.indexes || []).filter(ix => ix.name !== '_id_').slice(0, 6).map(ix => (
                        <span key={ix.name} className={`px-1.5 py-0.5 rounded font-mono ${ix.unique ? 'bg-emerald-900/50 text-emerald-300' : 'bg-[#0f0f1e] text-gray-400'}`} title={ix.unique ? 'unique' : ''}>{ix.name}</span>
                      ))}
                      {(c.indexes || []).length > 7 && <span className="text-gray-600">+{c.indexes.length - 7}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {risk.startsWith('HIGH') ? <span className="bg-rose-500/20 text-rose-300 px-2 py-0.5 rounded-full text-[10px] font-bold">HIGH</span>
                      : risk.startsWith('MEDIUM') ? <span className="bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full text-[10px] font-bold">MEDIUM</span>
                      : <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full text-[10px] font-bold">OK</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-[#0f0f1e] border border-violet-500/20 rounded-xl p-4 text-xs text-gray-400">
        <p className="font-bold text-violet-300 mb-2">Como interpretar:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong className="text-white">MT</strong> = Collection multi-tenant (deve ter indice de tenant_id no primeiro campo)</li>
          <li><strong className="text-white">GLOBAL</strong> = Collection compartilhada entre tenants (tenants, plans, business_types) — sem necessidade de tenant_id</li>
          <li><strong className="text-rose-300">HIGH RISK</strong> = Collection MT com &gt;10k docs SEM indice de tenant_id (criticar — corrigir antes de escalar)</li>
          <li><strong className="text-amber-300">MEDIUM RISK</strong> = Collection MT com &gt;1k docs SEM indice (adicionar em breve)</li>
        </ul>
      </div>
    </div>
  );
};


/* ============ TENANT PURGE (iter77bt) ============ */
const TenantPurgePage = () => {
  const [tenants, setTenants] = useState([]);
  const [targets, setTargets] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // iter148: acoes especiais destrutivas por tenant (dupla confirmacao no click).
  const [specialBusy, setSpecialBusy] = useState(false);
  // iter77dc.10: historico de operacoes destrutivas + reverter
  const [ops, setOps] = useState([]);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsBusy, setOpsBusy] = useState(null); // op_id em restauracao
  const loadOps = async (tid) => {
    if (!tid) { setOps([]); return; }
    setOpsLoading(true);
    try {
      const { data } = await axios.get(`${SA}/tenants/${tid}/ops`, { withCredentials: true });
      setOps(data?.data || []);
    } catch { setOps([]); }
    finally { setOpsLoading(false); }
  };
  const restoreOp = async (opId) => {
    const t = tenants.find(x => x.id === selectedTenant);
    if (!t) return;
    if (!window.confirm(`Reverter esta operacao?\n\nOs produtos + estoques originais serao restaurados com os mesmos IDs.`)) return;
    setOpsBusy(opId);
    try {
      const { data } = await axios.post(`${SA}/tenants/${t.id}/ops/${opId}/restore`, {}, { withCredentials: true });
      toast.success(`Restaurados: ${data.restored_products} produtos, ${data.restored_stock} estoques, ${data.restored_prices} precos${data.errors_count ? ` (${data.errors_count} erros)` : ''}`);
      loadOps(t.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao restaurar');
    } finally { setOpsBusy(null); }
  };
  const runZeroStockAllCompanies = async () => {
    const tenant = tenants.find(t => t.id === selectedTenant);
    if (!tenant) { toast.info('Selecione um tenant primeiro'); return; }
    const label = tenant.company_name || tenant.subdomain;
    if (!window.confirm(`Zerar estoque de TODOS os produtos em TODAS as filiais do tenant "${label}"?\n\nO cadastro dos produtos NAO sera afetado — apenas o estoque de cada filial vira 0.`)) return;
    const confirmTxt = window.prompt(`Para confirmar, digite o subdominio do tenant: "${tenant.subdomain}"`);
    if ((confirmTxt || '').trim() !== tenant.subdomain) { toast.info('Cancelado — texto de confirmacao nao bate'); return; }
    setSpecialBusy(true);
    try {
      const { data } = await axios.post(`${SA}/tenants/${tenant.id}/products/zero-stock-all-companies`, {}, { withCredentials: true });
      const total = Number(data.expected ?? data.zeroed ?? 0);
      toast.success(`Estoque zerado em ${data.companies} filial(is) x ${data.products} produto(s) = ${total} rows.`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao zerar estoque');
    } finally { setSpecialBusy(false); }
  };
  const runDeleteInactiveProducts = async () => {
    const tenant = tenants.find(t => t.id === selectedTenant);
    if (!tenant) { toast.info('Selecione um tenant primeiro'); return; }
    const label = tenant.company_name || tenant.subdomain;
    if (!window.confirm(`Apagar TODOS os produtos INATIVOS do tenant "${label}"?\n\nEsta acao NAO pode ser desfeita. Produtos ATIVOS e historico de vendas nao serao afetados.`)) return;
    const confirmTxt = window.prompt(`Para confirmar, digite o subdominio do tenant: "${tenant.subdomain}"`);
    if ((confirmTxt || '').trim() !== tenant.subdomain) { toast.info('Cancelado — texto de confirmacao nao bate'); return; }
    setSpecialBusy(true);
    try {
      const { data } = await axios.post(`${SA}/tenants/${tenant.id}/products/delete-inactive`, {}, { withCredentials: true });
      toast.success(`${data.deleted} produto(s) inativo(s) apagados. ${data.product_stock_rows_removed} row(s) de estoque removidas.`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao apagar inativos');
    } finally { setSpecialBusy(false); }
  };
  // iter156ei: Corrige drift de compartilhamento em lote.
  // Percorre TODOS os produtos do tenant e reescreve `company_ids` com a
  // UNIAO entre `company_ids` normalizado e as filiais com row em `product_stock`.
  // Nao altera produtos shared-all (company_ids=[]) nem produtos sem sinal.
  const runHealProductShares = async () => {
    const tenant = tenants.find(t => t.id === selectedTenant);
    if (!tenant) { toast.info('Selecione um tenant primeiro'); return; }
    const label = tenant.company_name || tenant.subdomain;
    if (!window.confirm(`Corrigir DRIFT de compartilhamento de produtos do tenant "${label}"?\n\nO endpoint percorre TODOS os produtos e reescreve o campo company_ids com a lista real de filiais compartilhadas (baseado em product_stock + company_ids normalizado).\n\nProdutos shared-all (visiveis em todas as filiais) NAO sao alterados. Idempotente.`)) return;
    setSpecialBusy(true);
    try {
      const { data } = await axios.post(`${SA}/tenants/${tenant.id}/products/heal-shares`, {}, { withCredentials: true });
      toast.success(`Analisados: ${data.total} | Corrigidos: ${data.healed} | Ja OK: ${data.kept_ok} | Shared-all: ${data.kept_shared_all} | Sem sinal: ${data.no_signal}`, { duration: 8000 });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao corrigir compartilhamentos');
    } finally { setSpecialBusy(false); }
  };
  // iter156ek: Unifica produtos DUPLICADOS por SKU em lote (varios _id com mesmo SKU
  // -> 1 produto compartilhado com todas as filiais dos irmaos). Preserva o estoque
  // de cada filial (soma se colisao).
  const runMergeProductSiblings = async () => {
    const tenant = tenants.find(t => t.id === selectedTenant);
    if (!tenant) { toast.info('Selecione um tenant primeiro'); return; }
    const label = tenant.company_name || tenant.subdomain;
    if (!window.confirm(`Unificar produtos DUPLICADOS (mesmo SKU) do tenant "${label}"?\n\nAcao: agrupa produtos por SKU. Se houver mais de 1 com o mesmo SKU, elege o mais antigo como MASTER e transfere estoque + precos dos duplicados para ele. Os duplicados sao APAGADOS.\n\nO estoque de cada filial e PRESERVADO (soma quando ha colisao). Produtos com SKU unico NAO sao afetados. Idempotente.\n\nNao pode ser desfeito.`)) return;
    const confirmTxt = window.prompt(`Para confirmar, digite o subdominio do tenant: "${tenant.subdomain}"`);
    if ((confirmTxt || '').trim() !== tenant.subdomain) { toast.info('Cancelado — texto de confirmacao nao bate'); return; }
    setSpecialBusy(true);
    try {
      const { data } = await axios.post(`${SA}/tenants/${tenant.id}/products/merge-siblings`, {}, { withCredentials: true });
      toast.success(`Grupos analisados: ${data.groups_analyzed} | Merges aplicados: ${data.merges_applied} | Irmaos deletados: ${data.siblings_deleted} | Stock rows transferidas: ${data.stock_rows_transferred}`, { duration: 10000 });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao unificar produtos duplicados');
    } finally { setSpecialBusy(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [t, tg] = await Promise.all([
          axios.get('/api/superadmin/tenants', { withCredentials: true }),
          axios.get('/api/superadmin/purge-targets', { withCredentials: true }),
        ]);
        setTenants(t.data.data || []);
        setTargets(tg.data || []);
      } catch (e) { toast.error('Erro ao carregar'); }
      finally { setLoading(false); }
    })();
  }, []);

  const toggleTarget = (k) => {
    setSelectedTargets(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
    setPreview(null);
  };

  const runDryRun = async () => {
    if (!selectedTenant || selectedTargets.length === 0) return toast.error('Selecione o tenant e ao menos um tipo de dado');
    setBusy(true);
    try {
      const { data } = await axios.post('/api/superadmin/purge', {
        tenant_id: selectedTenant,
        targets: selectedTargets,
        confirm_text: 'CONFIRMAR EXCLUSAO',
        dry_run: true,
      }, { withCredentials: true });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    } finally { setBusy(false); }
  };

  const runPurge = async () => {
    if (confirmText.trim().toUpperCase() !== 'CONFIRMAR EXCLUSAO') return toast.error('Digite exatamente: CONFIRMAR EXCLUSAO');
    if (!preview) return toast.error('Rode o "Preview" primeiro para verificar o impacto');
    setBusy(true);
    try {
      const { data } = await axios.post('/api/superadmin/purge', {
        tenant_id: selectedTenant,
        targets: selectedTargets,
        confirm_text: 'CONFIRMAR EXCLUSAO',
        dry_run: false,
      }, { withCredentials: true });
      toast.success(`Exclusao concluida: ${data.total_deleted} documentos apagados`);
      setPreview(null);
      setConfirmText('');
      setSelectedTargets([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    } finally { setBusy(false); }
  };

  if (loading) return <Loader />;
  const tenantName = tenants.find(t => t.id === selectedTenant)?.company_name || tenants.find(t => t.id === selectedTenant)?.subdomain || '';

  return (
    <div className="space-y-6" data-testid="tenant-purge-page">
      <div className="bg-rose-900/40 border border-rose-500/40 rounded-2xl p-4 flex items-start gap-3">
        <AlertTriangle className="text-rose-300 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <h2 className="text-lg font-black text-rose-100">Limpeza Seletiva de Dados do Tenant</h2>
          <p className="text-xs text-rose-200/80 mt-1">Ferramenta destrutiva. Voce pode apagar <strong>movimentos</strong> (vendas, orcamentos, financeiro, compras, etc) e tambem <strong>cadastros</strong> (produtos, clientes, fornecedores) — marcados em vermelho abaixo. Cadastros de <strong>empresas e usuarios</strong> NAO sao afetados. Sempre rode o Preview antes de confirmar.</p>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
        <label className="block text-xs font-bold text-white/70 mb-2">1. Escolha o Tenant</label>
        <select value={selectedTenant} onChange={e => { setSelectedTenant(e.target.value); setPreview(null); loadOps(e.target.value); }} className="w-full bg-slate-900 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" data-testid="purge-tenant-select">
          <option value="">— Selecionar —</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{`${t.company_name || t.subdomain} (${t.subdomain})`}</option>)}
        </select>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
        <label className="block text-xs font-bold text-white/70 mb-2">2. Escolha os tipos de dados a apagar</label>
        {/* iter77ct.34: agrupa movimentos vs cadastros (cadastros sao mais perigosos) */}
        {targets.some(t => !t.is_cadastro) && (
          <div className="mb-3">
            <p className="text-[10px] font-black text-white/50 uppercase tracking-wider mb-1.5">Movimentos (recomendado)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {targets.filter(t => !t.is_cadastro).map(t => (
                <label key={t.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${selectedTargets.includes(t.key) ? 'bg-rose-500/20 border-rose-400/60' : 'bg-slate-900 border-white/10 hover:border-white/20'}`}>
                  <input type="checkbox" checked={selectedTargets.includes(t.key)} onChange={() => toggleTarget(t.key)} className="accent-rose-500" data-testid={`purge-target-${t.key}`} />
                  <span className="text-xs text-white/90">{t.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        {targets.some(t => t.is_cadastro) && (
          <div>
            <p className="text-[10px] font-black text-rose-300 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
              Cadastros (destrutivo — apaga base de referencia)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {targets.filter(t => t.is_cadastro).map(t => (
                <label key={t.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${selectedTargets.includes(t.key) ? 'bg-rose-600/30 border-rose-400' : 'bg-rose-950/20 border-rose-500/20 hover:border-rose-500/50'}`}>
                  <input type="checkbox" checked={selectedTargets.includes(t.key)} onChange={() => toggleTarget(t.key)} className="accent-rose-500" data-testid={`purge-target-${t.key}`} />
                  <span className="text-xs text-white/90">{t.label}</span>
                  <span className="ml-auto text-[9px] font-black text-rose-200 bg-rose-800/50 px-1.5 py-0.5 rounded-full border border-rose-500/50">CADASTRO</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 items-end flex-wrap">
        <button onClick={runDryRun} disabled={busy || !selectedTenant || selectedTargets.length === 0} className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold text-sm flex items-center gap-2" data-testid="purge-preview-btn">
          <RefreshCw size={14} /> Preview (contar sem apagar)
        </button>
      </div>

      {preview && (
        <div className="bg-slate-900/60 border border-cyan-500/30 rounded-2xl p-4" data-testid="purge-preview-result">
          <h3 className="text-sm font-black text-cyan-200 mb-3">Preview do impacto em: <strong>{tenantName}</strong></h3>
          <table className="w-full text-xs">
            <thead className="border-b border-white/10 text-white/60">
              <tr><th className="text-left px-2 py-1">Tipo</th><th className="text-right px-2 py-1">Registros que serao apagados</th></tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {Object.entries(preview.deleted).map(([k, v]) => (
                <tr key={k}>
                  <td className="px-2 py-1 text-white/90">{targets.find(t => t.key === k)?.label || k}</td>
                  <td className="px-2 py-1 text-right font-mono text-cyan-200 font-bold">{v}</td>
                </tr>
              ))}
              <tr className="bg-white/5">
                <td className="px-2 py-1.5 text-white font-black">TOTAL</td>
                <td className="px-2 py-1.5 text-right font-mono text-rose-300 font-black">{preview.total_deleted}</td>
              </tr>
            </tbody>
          </table>

          <div className="mt-4 pt-4 border-t border-white/10">
            <label className="block text-xs font-bold text-rose-200 mb-1.5">3. Digite <code className="bg-black/40 px-1.5 py-0.5 rounded text-rose-100 border border-rose-500/40">CONFIRMAR EXCLUSAO</code> para liberar o botao</label>
            <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="CONFIRMAR EXCLUSAO" className="w-full bg-slate-900 border border-rose-500/40 rounded-lg px-3 py-2 text-white text-sm font-mono" data-testid="purge-confirm-text" />
            <button onClick={runPurge} disabled={busy || confirmText.trim().toUpperCase() !== 'CONFIRMAR EXCLUSAO'} className="mt-3 px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-30 text-white font-black text-sm flex items-center gap-2" data-testid="purge-execute-btn">
              <Trash2 size={14} /> APAGAR {preview.total_deleted} REGISTROS
            </button>
          </div>
        </div>
      )}

      {/* iter148: Acoes Especiais — Zerar estoque global do tenant + Apagar produtos inativos */}
      {selectedTenant && (
        <div className="bg-amber-900/30 border border-amber-500/40 rounded-2xl p-4 space-y-3" data-testid="purge-special-actions">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-300 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <h3 className="text-sm font-black text-amber-100">Ações Especiais neste Tenant</h3>
              <p className="text-[11px] text-amber-200/80 mt-0.5">Operações destrutivas por tenant — pedem dupla confirmação (dialog + prompt do subdomain).</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={runZeroStockAllCompanies}
              disabled={specialBusy}
              className="flex items-start gap-3 p-3 rounded-lg bg-slate-900 border border-amber-500/40 hover:border-amber-400 hover:bg-amber-950/40 disabled:opacity-50 disabled:cursor-not-allowed text-left transition-colors"
              data-testid="purge-zero-stock-btn"
            >
              <div className="w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                <Database className="text-amber-300" size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black text-amber-100">Zerar estoque em todas as filiais</div>
                <div className="text-[10px] text-amber-200/70 mt-0.5">Upsert stock=0 para cada (produto, filial ativa). Cadastro dos produtos permanece.</div>
              </div>
            </button>
            <button
              onClick={runDeleteInactiveProducts}
              disabled={specialBusy}
              className="flex items-start gap-3 p-3 rounded-lg bg-slate-900 border border-rose-500/40 hover:border-rose-400 hover:bg-rose-950/40 disabled:opacity-50 disabled:cursor-not-allowed text-left transition-colors"
              data-testid="purge-delete-inactive-btn"
            >
              <div className="w-9 h-9 rounded-lg bg-rose-500/20 border border-rose-500/40 flex items-center justify-center flex-shrink-0">
                <Trash2 className="text-rose-300" size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black text-rose-100">Apagar produtos INATIVOS</div>
                <div className="text-[10px] text-rose-200/70 mt-0.5">Remove definitivamente todos os produtos com active=false + suas rows de estoque. Ativos e histórico não são afetados.</div>
              </div>
            </button>
            {/* iter156ei: Corrige drift de compartilhamento em lote (sem confirmacao dupla — nao destrutivo) */}
            <button
              onClick={runHealProductShares}
              disabled={specialBusy}
              className="flex items-start gap-3 p-3 rounded-lg bg-slate-900 border border-cyan-500/40 hover:border-cyan-400 hover:bg-cyan-950/40 disabled:opacity-50 disabled:cursor-not-allowed text-left transition-colors"
              data-testid="purge-heal-shares-btn"
            >
              <div className="w-9 h-9 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center flex-shrink-0">
                <RotateCcw className="text-cyan-300" size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black text-cyan-100">Corrigir compartilhamento (heal)</div>
                <div className="text-[10px] text-cyan-200/70 mt-0.5">Reescreve company_ids de todos os produtos com a lista real (union entre company_ids + product_stock). Idempotente. Shared-all nao muda.</div>
              </div>
            </button>
            {/* iter156ek: Unificar produtos duplicados por SKU (destrutivo — pede confirmacao dupla) */}
            <button
              onClick={runMergeProductSiblings}
              disabled={specialBusy}
              className="flex items-start gap-3 p-3 rounded-lg bg-slate-900 border border-fuchsia-500/40 hover:border-fuchsia-400 hover:bg-fuchsia-950/40 disabled:opacity-50 disabled:cursor-not-allowed text-left transition-colors"
              data-testid="purge-merge-siblings-btn"
            >
              <div className="w-9 h-9 rounded-lg bg-fuchsia-500/20 border border-fuchsia-500/40 flex items-center justify-center flex-shrink-0">
                <Boxes className="text-fuchsia-300" size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black text-fuchsia-100">Unificar produtos duplicados (mesmo SKU)</div>
                <div className="text-[10px] text-fuchsia-200/70 mt-0.5">Elege o mais antigo como master, transfere estoque de cada filial e apaga os duplicados. Estoque preservado por filial.</div>
              </div>
            </button>
          </div>
          {/* iter77dc.10: Historico de operacoes destrutivas + Reverter */}
          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-xs font-black text-emerald-100 uppercase tracking-wider flex items-center gap-2">
                  <RotateCcw size={12} /> Historico de Operacoes (Reverter)
                </div>
                <div className="text-[10px] text-emerald-200/60 mt-0.5">
                  Todas as operacoes destrutivas dos ultimos 30 dias podem ser revertidas com 1 clique. Snapshots expiram automaticamente.
                </div>
              </div>
              <button onClick={() => loadOps(selectedTenant)} disabled={opsLoading} className="text-[10px] text-emerald-200/80 hover:text-white underline disabled:opacity-40" data-testid="ops-refresh-btn">
                {opsLoading ? 'Carregando...' : 'Atualizar'}
              </button>
            </div>
            {ops.length === 0 && !opsLoading && (
              <div className="p-3 rounded-lg bg-slate-900 border border-white/10 text-[11px] text-slate-400 text-center">
                Nenhuma operacao destrutiva registrada nesse tenant.
              </div>
            )}
            <div className="space-y-2">
              {ops.map(o => (
                <div key={o.id} className={`p-3 rounded-lg border ${o.restored ? 'bg-emerald-950/30 border-emerald-500/30' : 'bg-slate-900 border-white/10'}`} data-testid={`op-row-${o.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${o.op_type === 'delete-inactive' ? 'bg-rose-500/20 text-rose-200' : o.op_type === 'zero-stock-all-companies' ? 'bg-amber-500/20 text-amber-200' : 'bg-fuchsia-500/20 text-fuchsia-200'}`}>
                          {o.op_type}
                        </span>
                        {o.restored && (
                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/30 text-emerald-200">Restaurado</span>
                        )}
                        <span className="text-[10px] text-slate-400">
                          {new Date(o.created_at).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      <div className="text-xs font-semibold text-slate-200 mt-1 truncate">{o.description}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        Produtos: <strong className="text-slate-200">{o.counts?.products || 0}</strong> · Estoques: <strong className="text-slate-200">{o.counts?.product_stock || 0}</strong> · Precos: <strong className="text-slate-200">{o.counts?.prices || 0}</strong>
                        {o.created_by ? <span className="ml-2 opacity-70">por {o.created_by}</span> : null}
                      </div>
                      {o.restored && o.restore_summary && (
                        <div className="text-[10px] text-emerald-300/80 mt-1">
                          ✓ Restaurados: {o.restore_summary.restored_products} produtos, {o.restore_summary.restored_stock} estoques, {o.restore_summary.restored_prices} precos
                          {o.restore_summary.errors_count ? ` (${o.restore_summary.errors_count} erros)` : ''}
                        </div>
                      )}
                    </div>
                    {!o.restored && (
                      <button
                        onClick={() => restoreOp(o.id)}
                        disabled={opsBusy === o.id}
                        className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-[11px] font-bold flex items-center gap-1.5 flex-shrink-0"
                        data-testid={`op-restore-${o.id}`}
                      >
                        <RotateCcw size={11} />
                        {opsBusy === o.id ? 'Restaurando...' : 'Reverter'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// iter77ct.52 + 52b: SuperAdmin — ajuste de datas de vendas em massa
// Fluxo simplificado (52b): Empresa (tenant) -> Filial -> Nº inicial -> Nº final -> Nova data.
// Casos de uso: importacao com timezone errado, correcao de vendas antigas, retificacao de fechamento.
const SalesDateAdjustPage = () => {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [saleNumberStart, setSaleNumberStart] = useState('');
  const [saleNumberEnd, setSaleNumberEnd] = useState('');
  const [preview, setPreview] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [newDate, setNewDate] = useState('');
  const [alsoDeps, setAlsoDeps] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // iter77ct.52c: backend retorna {"data": tenants} — nao {"tenants": [...]}
        const { data } = await axios.get(`${SA}/tenants`, { withCredentials: true });
        setTenants(data.data || data.tenants || []);
      } catch { /* ignore */ }
    })();
  }, []);

  // iter77ct.52b: quando muda o tenant, recarrega as filiais e limpa selecoes
  useEffect(() => {
    if (!tenantId) { setCompanies([]); setCompanyId(''); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${SA}/tenants/${tenantId}/companies`, { withCredentials: true });
        setCompanies(data.data || []);
      } catch { setCompanies([]); }
      setCompanyId('');
      setPreview(null);
      setResult(null);
    })();
  }, [tenantId]);

  const runPreview = async () => {
    if (!tenantId) return toast.error('Selecione uma empresa (tenant)');
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${SA}/sales/date-adjust/preview`, {
        tenant_id: tenantId,
        company_id: companyId || null,
        sale_number_start: saleNumberStart ? parseInt(saleNumberStart, 10) : null,
        sale_number_end: saleNumberEnd ? parseInt(saleNumberEnd, 10) : null,
        limit_preview: 2000,
      }, { withCredentials: true });
      setPreview(data);
      // iter77ct.52b: pre-seleciona todas as vendas retornadas (usuario ja escolheu a faixa por numero).
      setSelectedIds(new Set((data.preview_rows || []).map(r => r.id)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao carregar preview');
    } finally { setBusy(false); }
  };

  const applyAdjust = async () => {
    if (!newDate) return toast.error('Informe a nova data');
    if (selectedIds.size === 0) return toast.error('Selecione ao menos 1 venda');
    const ok = await confirmDialog({
      title: 'Alterar data de vendas',
      message: `Confirmar alteracao da data de ${selectedIds.size} venda(s) para ${newDate}? Esta acao sera registrada no audit log e afetara contas a receber/pagar/lancamentos contabeis vinculados.`,
      confirmText: 'Aplicar', danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${SA}/sales/date-adjust/apply`, {
        tenant_id: tenantId,
        sale_ids: Array.from(selectedIds),
        new_date: newDate,
        also_update_dependencies: alsoDeps,
      }, { withCredentials: true });
      setResult(data);
      setPreview(null);
      setSelectedIds(new Set());
      toast.success(`${data.updated} venda(s) atualizadas`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao aplicar');
    } finally { setBusy(false); }
  };

  // iter156dt6: exclui as vendas selecionadas — estorna estoque + cancela financeiro.
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return toast.error('Selecione ao menos 1 venda');
    const ok = await confirmDialog({
      title: 'Excluir vendas selecionadas',
      message: `Voce esta prestes a EXCLUIR ${selectedIds.size} venda(s). Esta acao:\n\n- Estorna o estoque (produtos vendidos voltam ao estoque da filial)\n- Cancela lancamentos financeiros vinculados (contas a receber/pagar)\n- Estorna saldo das contas banco\n- Reverte cashback acumulado\n- Nao afeta vendas com NF-e autorizada em producao (serao ignoradas)\n\nEsta acao NAO pode ser desfeita. Confirma?`,
      confirmText: 'Excluir', danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${SA}/sales/bulk-delete`, {
        tenant_id: tenantId,
        sale_ids: Array.from(selectedIds),
      }, { withCredentials: true });
      setPreview(null);
      setSelectedIds(new Set());
      let msg = `${data.deleted} venda(s) excluida(s).`;
      const skipped = (data.skipped_locked_by_invoice || 0) + (data.skipped_active_returns || 0) + (data.skipped_already_cancelled || 0);
      if (skipped > 0) msg += ` ${skipped} ignorada(s) (NF-e emitida ou com devolucoes).`;
      if (data.errors?.length > 0) msg += ` ${data.errors.length} erro(s).`;
      toast.success(msg);
      setResult({
        __bulk_delete: true,
        deleted: data.deleted,
        requested: data.requested,
        skipped_locked_by_invoice: data.skipped_locked_by_invoice,
        skipped_active_returns: data.skipped_active_returns,
        skipped_already_cancelled: data.skipped_already_cancelled,
        errors: data.errors || [],
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao excluir vendas');
    } finally { setBusy(false); }
  };

  const toggleId = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const toggleAll = () => {
    if (!preview?.preview_rows?.length) return;
    if (selectedIds.size === preview.preview_rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(preview.preview_rows.map(r => r.id)));
  };

  return (
    <div className="space-y-6" data-testid="sales-date-adjust-page">
      <div>
        <h1 className="text-3xl font-bold text-white">Ajuste de Datas de Vendas</h1>
        <p className="text-slate-400 text-sm mt-1">Ferramenta de correcao em massa — apenas SuperAdmin. Vendas ligadas a NF-e autorizada em producao sao ignoradas.</p>
      </div>

      <div className="bg-[#16162a] border border-[#2a2a4a] rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1">Empresa (Tenant)</label>
            <select value={tenantId} onChange={e => setTenantId(e.target.value)} className="w-full bg-slate-900 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" data-testid="date-adjust-tenant-select">
              <option value="">— Selecione —</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.company_name || t.subdomain}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1">Filial</label>
            <select value={companyId} onChange={e => setCompanyId(e.target.value)} disabled={!tenantId} className="w-full bg-slate-900 border border-white/20 rounded-lg px-3 py-2 text-white text-sm disabled:opacity-40" data-testid="date-adjust-company-select">
              <option value="">— Todas as filiais —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.code ? `${c.code} · ` : ''}{c.name}{c.is_matriz ? ' (matriz)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1">Nº venda inicial</label>
            <input type="number" min="1" value={saleNumberStart} onChange={e => setSaleNumberStart(e.target.value)} placeholder="ex: 100" className="w-full bg-slate-900 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" data-testid="date-adjust-number-start" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1">Nº venda final</label>
            <input type="number" min="1" value={saleNumberEnd} onChange={e => setSaleNumberEnd(e.target.value)} placeholder="ex: 150" className="w-full bg-slate-900 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" data-testid="date-adjust-number-end" />
          </div>
          <div className="flex items-end">
            <button onClick={runPreview} disabled={busy || !tenantId} className="w-full px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold text-sm flex items-center justify-center gap-2" data-testid="date-adjust-preview-btn">
              <Eye className="w-4 h-4" /> Buscar
            </button>
          </div>
        </div>
        <p className="text-[11px] text-slate-500">Deixe filial vazia para buscar em todas. Deixe o range de numero vazio para buscar todas as vendas do tenant/filial.</p>
      </div>

      {preview && (
        <div className="bg-[#16162a] border border-[#2a2a4a] rounded-xl p-6 space-y-4" data-testid="date-adjust-preview">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm text-slate-400"><strong className="text-white">{preview.tenant_name}</strong> — {preview.total} venda(s) no filtro, {preview.preview_count} exibida(s)</p>
              {preview.grouped_by_date?.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">Distribuicao: {preview.grouped_by_date.slice(0, 8).map(g => `${g.date} (${g.count})`).join(' · ')}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={toggleAll} className="px-3 py-1.5 text-xs rounded-lg bg-slate-800 hover:bg-slate-700 text-white" data-testid="date-adjust-toggle-all">
                {selectedIds.size === preview.preview_rows.length ? 'Desmarcar tudo' : 'Selecionar tudo'} ({selectedIds.size})
              </button>
              {/* iter156dt6: exclusao em massa das vendas selecionadas */}
              <button
                onClick={bulkDelete}
                disabled={busy || selectedIds.size === 0}
                className="px-3 py-1.5 text-xs rounded-lg bg-rose-700 hover:bg-rose-600 disabled:opacity-40 text-white font-bold flex items-center gap-1.5"
                data-testid="date-adjust-bulk-delete-btn"
                title="Excluir as vendas selecionadas — estorna estoque e cancela financeiro"
              >
                <Trash2 className="w-3.5 h-3.5" /> Excluir Selecionadas ({selectedIds.size})
              </button>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[500px] overflow-y-auto border border-white/10 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left w-8"></th>
                  <th className="px-3 py-2 text-left text-slate-300">Nº</th>
                  <th className="px-3 py-2 text-left text-slate-300">Data / Hora</th>
                  <th className="px-3 py-2 text-left text-slate-300">Cliente</th>
                  <th className="px-3 py-2 text-right text-slate-300">Total</th>
                </tr>
              </thead>
              <tbody>
                {preview.preview_rows.map(r => (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-slate-800/30">
                    <td className="px-3 py-1.5">
                      <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggleId(r.id)} className="accent-cyan-500" data-testid={`date-adjust-row-${r.id}`} />
                    </td>
                    <td className="px-3 py-1.5 text-slate-300">#{r.number}</td>
                    <td className="px-3 py-1.5 text-slate-300 font-mono text-xs">{r.created_at ? new Date(r.created_at).toLocaleString('pt-BR') : '—'}</td>
                    <td className="px-3 py-1.5 text-slate-400">{r.customer_name || 'Consumidor'}</td>
                    <td className="px-3 py-1.5 text-slate-300 text-right font-mono">R$ {(r.total || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pt-4 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">Nova data (aplicada a todas selecionadas)</label>
              <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="w-full bg-slate-900 border border-amber-400/40 rounded-lg px-3 py-2 text-white text-sm" data-testid="date-adjust-new-date" />
              <p className="text-[10px] text-slate-500 mt-1">Hora original (HH:MM:SS) e preservada.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 pt-6">
              <input type="checkbox" checked={alsoDeps} onChange={e => setAlsoDeps(e.target.checked)} className="accent-cyan-500" data-testid="date-adjust-also-deps" />
              Atualizar dependencias (receber/pagar/lancamentos)
            </label>
            <div className="flex items-end">
              <button onClick={applyAdjust} disabled={busy || selectedIds.size === 0 || !newDate} className="w-full px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white font-bold text-sm flex items-center justify-center gap-2" data-testid="date-adjust-apply-btn">
                <AlertTriangle className="w-4 h-4" /> Aplicar ({selectedIds.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {result && !result.__bulk_delete && (
        <div className="bg-emerald-900/20 border border-emerald-500/40 rounded-xl p-6" data-testid="date-adjust-result">
          <p className="text-emerald-300 font-bold">Ajuste aplicado com sucesso</p>
          <ul className="text-sm text-emerald-100 mt-2 space-y-1">
            <li>Vendas atualizadas: <strong>{result.updated}</strong> de {result.requested} solicitadas</li>
            <li>Nova data aplicada: <strong>{result.new_date}</strong></li>
            {result.skipped_locked_by_invoice > 0 && <li className="text-amber-300">Ignoradas (NF-e autorizada): {result.skipped_locked_by_invoice}</li>}
            <li>Dependencias atualizadas: {result.dependencies_updated?.receivables} contas a receber · {result.dependencies_updated?.expenses} contas a pagar · {result.dependencies_updated?.journal_entries} lancamentos</li>
          </ul>
        </div>
      )}

      {result?.__bulk_delete && (
        <div className="bg-rose-900/20 border border-rose-500/40 rounded-xl p-6" data-testid="bulk-delete-result">
          <p className="text-rose-300 font-bold">Exclusao em massa concluida</p>
          <ul className="text-sm text-rose-100 mt-2 space-y-1">
            <li>Vendas excluidas: <strong className="text-emerald-300">{result.deleted}</strong> de {result.requested} solicitadas</li>
            {result.skipped_locked_by_invoice > 0 && <li className="text-amber-300">Ignoradas (NF-e autorizada em producao): {result.skipped_locked_by_invoice}</li>}
            {result.skipped_active_returns > 0 && <li className="text-amber-300">Ignoradas (com devolucoes ativas): {result.skipped_active_returns}</li>}
            {result.skipped_already_cancelled > 0 && <li className="text-slate-300">Ja canceladas: {result.skipped_already_cancelled}</li>}
            {result.errors?.length > 0 && (
              <li className="text-rose-300">Erros: {result.errors.length}
                <ul className="ml-4 mt-1 text-xs text-slate-300 space-y-0.5">
                  {result.errors.slice(0, 10).map((er, i) => (
                    <li key={i}>#{er.number || er.sale_id}: {er.reason}</li>
                  ))}
                  {result.errors.length > 10 && <li>...e mais {result.errors.length - 10}</li>}
                </ul>
              </li>
            )}
            <li className="text-slate-300 pt-2 border-t border-white/10 mt-2">
              Efeitos aplicados em cada venda excluida: estoque estornado, financeiro cancelado, saldo banco estornado, cashback revertido.
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

/* ===================== iter77dc.11: BackupPage ===================== */
const BackupPage = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [tenants, setTenants] = useState([]);
  // restore wizard state
  const [rSelected, setRSelected] = useState(null); // filename
  const [rTenantId, setRTenantId] = useState('');
  const [rCompanies, setRCompanies] = useState([]); // ids selecionadas
  const [rCompaniesList, setRCompaniesList] = useState([]); // opcoes carregadas
  const [rCollections, setRCollections] = useState([]);
  const [rMode, setRMode] = useState('upsert');
  const [rBusy, setRBusy] = useState(false);
  const [rResult, setRResult] = useState(null);

  const COLLECTION_GROUPS = [
    { key: 'products', label: 'Produtos' },
    { key: 'product_stock', label: 'Estoque (product_stock)' },
    { key: 'erp_product_prices', label: 'Precos por Filial' },
    { key: 'erp_clients', label: 'Clientes' },
    { key: 'erp_suppliers', label: 'Fornecedores' },
    { key: 'erp_sales', label: 'Vendas / Pedidos / Orcamentos' },
    { key: 'erp_financial', label: 'Financeiro (contas)' },
    { key: 'erp_stock_movements', label: 'Movimentacao Estoque' },
    { key: 'erp_stock_transfers', label: 'Transferencia Estoque' },
    { key: 'erp_purchases', label: 'Compras' },
    { key: 'erp_invoices', label: 'NF-e / NFC-e' },
    { key: 'erp_categories', label: 'Categorias' },
    { key: 'erp_companies', label: 'Empresas/Filiais (cadastro)' },
    { key: 'erp_payment_methods', label: 'Formas de Pagamento' },
    { key: 'erp_chart_of_accounts', label: 'Plano de Contas' },
    { key: 'erp_bank_accounts', label: 'Contas Bancarias' },
    { key: 'erp_natureza_operacao', label: 'Natureza de Operacao' },
    { key: 'erp_cost_centers', label: 'Centros de Custo' },
    { key: 'erp_price_tables', label: 'Tabelas de Preco' },
    { key: 'erp_label_templates', label: 'Etiquetas' },
    { key: 'erp_company_settings', label: 'Configuracoes da Empresa' },
    { key: 'users', label: 'Usuarios' },
  ];

  const loadBackups = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${SA}/backup/list`, { withCredentials: true });
      setBackups(data?.data || []);
    } catch { toast.error('Erro ao listar backups'); }
    finally { setLoading(false); }
  };
  const loadTenants = async () => {
    try {
      const { data } = await axios.get(`${SA}/tenants?limit=500`, { withCredentials: true });
      setTenants(data?.data || data || []);
    } catch { setTenants([]); }
  };
  useEffect(() => { loadBackups(); loadTenants(); }, []);
  useEffect(() => {
    if (!rTenantId) { setRCompaniesList([]); return; }
    // busca as empresas/filiais desse tenant via superadmin API
    axios.get(`${SA}/tenants/${rTenantId}/companies`, { withCredentials: true })
      .then(r => setRCompaniesList(r.data?.data || r.data || []))
      .catch(() => setRCompaniesList([]));
  }, [rTenantId]);

  const runNow = async () => {
    setRunning(true);
    try {
      // iter156x: endpoint agora retorna imediatamente (background task).
      // Precisamos aguardar ~5s antes de recarregar a lista para o arquivo aparecer.
      const { data } = await axios.post(`${SA}/backup/run-now`, {}, { withCredentials: true });
      if (data.status === 'started') {
        toast.info(data.message || 'Backup iniciado em segundo plano. Aguarde e clique em Atualizar.');
        // Poll da lista a cada 5s por ate 90s
        let tries = 0;
        const poll = setInterval(async () => {
          tries += 1;
          try {
            const before = backups.map(b => b.filename);
            const r = await axios.get(`${SA}/backup/list`, { withCredentials: true });
            const list = r.data?.data || [];
            const newOne = list.find(b => !before.includes(b.filename));
            if (newOne) {
              toast.success(`Backup criado: ${newOne.filename} (${newOne.size} bytes)`);
              setBackups(list);
              clearInterval(poll);
              setRunning(false);
              return;
            }
            if (tries >= 18) { // 90s
              clearInterval(poll);
              setRunning(false);
              loadBackups();
            }
          } catch {
            clearInterval(poll);
            setRunning(false);
          }
        }, 5000);
        return; // nao roda o finally imediato
      }
      // Compat: se retorno legado sincrono
      toast.success(`Backup criado: ${data.filename} (${data.total_docs} docs)`);
      loadBackups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar backup'); }
    finally { setRunning(false); }
  };
  const downloadBackup = (fname) => {
    window.open(`${SA}/backup/download/${encodeURIComponent(fname)}`, '_blank');
  };
  const deleteBackup = async (fname) => {
    if (!window.confirm(`Apagar ${fname}?`)) return;
    try {
      await axios.delete(`${SA}/backup/${encodeURIComponent(fname)}`, { withCredentials: true });
      toast.success('Backup apagado');
      loadBackups();
    } catch { toast.error('Erro ao apagar'); }
  };
  const uploadBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${SA}/backup/upload`, fd, { withCredentials: true });
      toast.success('Upload OK');
      loadBackups();
    } catch (er) { toast.error(er.response?.data?.detail || 'Erro no upload'); }
    e.target.value = '';
  };
  const toggleCol = (k) => setRCollections(rCollections.includes(k) ? rCollections.filter(x => x !== k) : [...rCollections, k]);
  const toggleComp = (id) => setRCompanies(rCompanies.includes(id) ? rCompanies.filter(x => x !== id) : [...rCompanies, id]);
  const runRestore = async () => {
    if (!rSelected) return;
    const summary = [
      `Arquivo: ${rSelected}`,
      `Tenant: ${rTenantId ? (tenants.find(t => t.id === rTenantId)?.company_name || rTenantId) : 'TODOS'}`,
      `Filiais: ${rCompanies.length > 0 ? rCompanies.length + ' selecionadas' : 'TODAS'}`,
      `Colecoes: ${rCollections.length > 0 ? rCollections.join(', ') : 'TODAS'}`,
      `Modo: ${rMode}`,
    ].join('\n');
    if (!window.confirm(`Restaurar com estas opcoes?\n\n${summary}\n\nEsta operacao substitui docs existentes com o mesmo _id.`)) return;
    setRBusy(true);
    setRResult(null);
    try {
      const { data } = await axios.post(`${SA}/backup/restore`, {
        filename: rSelected,
        tenant_id: rTenantId || null,
        company_ids: rCompanies.length > 0 ? rCompanies : null,
        collections: rCollections.length > 0 ? rCollections : null,
        mode: rMode,
      }, { withCredentials: true });
      setRResult(data);
      toast.success('Restauracao concluida');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro na restauracao');
    } finally { setRBusy(false); }
  };

  const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="p-4 md:p-8" data-testid="backup-page">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-white mb-1 flex items-center gap-2"><Save size={22} /> Backup & Restore</h1>
        <p className="text-sm text-gray-400">Backup diario automatico as 03h BRT. Retencao local: 7 arquivos. Integracao Google Drive: em breve.</p>
      </div>

      {/* Actions bar */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={runNow} disabled={running} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-bold flex items-center gap-2" data-testid="backup-run-now-btn">
          <RefreshCw size={14} className={running ? 'animate-spin' : ''} />
          {running ? 'Gerando...' : 'Executar Backup Agora'}
        </button>
        <label className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm font-bold flex items-center gap-2 cursor-pointer" data-testid="backup-upload-label">
          <Upload size={14} /> Enviar arquivo (.json.gz)
          <input type="file" accept=".gz,application/gzip" onChange={uploadBackup} className="hidden" />
        </label>
        <button onClick={loadBackups} className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-bold flex items-center gap-2">
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      {/* Backups list */}
      <div className="bg-[#16162a] border border-[#2a2a4a] rounded-2xl p-4 mb-6">
        <div className="text-xs font-black uppercase tracking-wider text-slate-400 mb-3">Backups disponiveis</div>
        {loading && <div className="text-sm text-slate-400">Carregando...</div>}
        {!loading && backups.length === 0 && <div className="text-sm text-slate-500">Nenhum backup ainda. Clique em "Executar Backup Agora" para criar o primeiro.</div>}
        <div className="space-y-2">
          {backups.map((b, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-white/5" data-testid={`backup-row-${b.filename}`}>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs text-slate-200 truncate">{b.filename}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {fmtSize(b.size)} · {new Date(b.modified_at).toLocaleString('pt-BR')}
                  {b.meta?.description && <> · <span className="text-slate-400">{b.meta.description}</span></>}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => setRSelected(b.filename)} className={`px-3 py-1.5 rounded-md text-[11px] font-bold ${rSelected === b.filename ? 'bg-violet-600 text-white' : 'bg-violet-500/20 text-violet-200 hover:bg-violet-500/30'}`} data-testid={`backup-select-${b.filename}`}>
                  {rSelected === b.filename ? 'Selecionado' : 'Selecionar p/ Restaurar'}
                </button>
                <button onClick={() => downloadBackup(b.filename)} className="p-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200" title="Download"><ExternalLink size={12} /></button>
                <button onClick={() => deleteBackup(b.filename)} className="p-1.5 rounded-md bg-rose-500/20 hover:bg-rose-500/40 text-rose-200" title="Apagar"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Restore wizard */}
      {rSelected && (
        <div className="bg-[#16162a] border-2 border-violet-500/30 rounded-2xl p-4" data-testid="backup-restore-wizard">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-black uppercase tracking-wider text-violet-300">Assistente de Restauracao</div>
            <button onClick={() => { setRSelected(null); setRResult(null); }} className="text-slate-400 hover:text-white text-xs">Cancelar</button>
          </div>
          <div className="p-3 rounded-lg bg-slate-900/50 mb-4">
            <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Arquivo selecionado</div>
            <div className="font-mono text-xs text-emerald-300">{rSelected}</div>
          </div>

          {/* Tenant */}
          <div className="mb-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">1. Restaurar para o tenant:</div>
            <select value={rTenantId} onChange={e => { setRTenantId(e.target.value); setRCompanies([]); }} className="w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="restore-tenant-select">
              <option value="">TODOS os tenants (nao recomendado)</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.company_name || t.name || t.subdomain}</option>)}
            </select>
          </div>

          {/* Companies */}
          {rTenantId && rCompaniesList.length > 0 && (
            <div className="mb-4">
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">2. Filiais afetadas (deixe vazio = TODAS):</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {rCompaniesList.map(c => (
                  <label key={c.id} className="flex items-center gap-2 text-xs text-slate-300 hover:bg-slate-800 p-1 rounded cursor-pointer">
                    <input type="checkbox" checked={rCompanies.includes(c.id)} onChange={() => toggleComp(c.id)} className="accent-violet-500" data-testid={`restore-company-${c.id}`} />
                    {c.name || c.nome_fantasia || c.id}
                    {c.is_matriz && <span className="text-[9px] text-cyan-400 font-bold">MATRIZ</span>}
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Aplica so em `product_stock` e `erp_product_prices`. Outras colecoes ignoram esse filtro.</div>
            </div>
          )}

          {/* Collections */}
          <div className="mb-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">3. O que restaurar (deixe vazio = TUDO):</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
              {COLLECTION_GROUPS.map(g => (
                <label key={g.key} className="flex items-center gap-2 text-xs text-slate-300 hover:bg-slate-800 p-1 rounded cursor-pointer">
                  <input type="checkbox" checked={rCollections.includes(g.key)} onChange={() => toggleCol(g.key)} className="accent-violet-500" data-testid={`restore-col-${g.key}`} />
                  <span className="truncate">{g.label}</span>
                  <span className="text-[9px] text-slate-500 font-mono">{g.key}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Mode */}
          <div className="mb-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">4. Modo:</div>
            <div className="flex gap-3">
              <label className="text-xs text-slate-300 flex items-center gap-2">
                <input type="radio" name="rMode" value="upsert" checked={rMode === 'upsert'} onChange={() => setRMode('upsert')} className="accent-violet-500" />
                Upsert (sobrescreve pelo _id — padrao)
              </label>
              <label className="text-xs text-slate-300 flex items-center gap-2">
                <input type="radio" name="rMode" value="insert-only" checked={rMode === 'insert-only'} onChange={() => setRMode('insert-only')} className="accent-violet-500" />
                Insert-only (nao sobrescreve existentes)
              </label>
            </div>
          </div>

          <button onClick={runRestore} disabled={rBusy} className="px-4 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-black flex items-center gap-2" data-testid="restore-run-btn">
            <RotateCcw size={14} className={rBusy ? 'animate-spin' : ''} />
            {rBusy ? 'Restaurando...' : 'Confirmar Restauracao'}
          </button>

          {rResult && (
            <div className="mt-4 p-3 rounded-lg bg-emerald-950/30 border border-emerald-500/30 text-xs text-emerald-200">
              <div className="font-black mb-1">Resultado:</div>
              <pre className="whitespace-pre-wrap font-mono text-[10px]">{JSON.stringify(rResult, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};



// iter77ct.105: Wrapper com tabs - "Ajustes e Exportacao"
const AdjustmentsExportsPage = () => {
  const [tab, setTab] = useState('sales-date');
  return (
    <div className="space-y-4" data-testid="adjustments-exports-page">
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-2">
        <button
          onClick={() => setTab('sales-date')}
          className={`px-4 py-2 rounded-t-lg text-sm font-bold transition ${tab === 'sales-date' ? 'bg-violet-600 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
          data-testid="tab-sales-date"
        >
          <CalendarIcon size={14} className="inline mr-1.5 -mt-0.5" />
          Ajuste de Datas de Vendas
        </button>
        <button
          onClick={() => setTab('financial-bulk-delete')}
          className={`px-4 py-2 rounded-t-lg text-sm font-bold transition ${tab === 'financial-bulk-delete' ? 'bg-violet-600 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
          data-testid="tab-financial-bulk-delete"
        >
          <Trash2 size={14} className="inline mr-1.5 -mt-0.5" />
          Excluir Lancamentos Financeiros
        </button>
        <button
          onClick={() => setTab('product-export')}
          className={`px-4 py-2 rounded-t-lg text-sm font-bold transition ${tab === 'product-export' ? 'bg-violet-600 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
          data-testid="tab-product-export"
        >
          <FileSpreadsheet size={14} className="inline mr-1.5 -mt-0.5" />
          Exportar Produtos (Excel)
        </button>
      </div>
      {tab === 'sales-date' && <SalesDateAdjustPage />}
      {tab === 'financial-bulk-delete' && <FinancialBulkDeletePage />}
      {tab === 'product-export' && <ProductExportPage />}
    </div>
  );
};


// iter156dv: SuperAdmin - Exclusao em massa de lancamentos financeiros
// Filtros: tenant, filial, apenas importados via planilha, periodo (X..Y), range de N.
const FinancialBulkDeletePage = () => {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [importedOnly, setImportedOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState(''); // iter156dx: '' = todos, 'cancelled' = ja-cancelados
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [numberFrom, setNumberFrom] = useState('');
  const [numberTo, setNumberTo] = useState('');
  const [hardDelete, setHardDelete] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const _SA = `${API}/superadmin`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${_SA}/tenants`, { withCredentials: true });
        setTenants(data.data || data.tenants || []);
      } catch { /* ignore */ }
    })();
  }, [_SA]);

  useEffect(() => {
    if (!tenantId) { setCompanies([]); setCompanyId(''); setPreview(null); setResult(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${_SA}/tenants/${tenantId}/companies`, { withCredentials: true });
        setCompanies(data.data || []);
      } catch { setCompanies([]); }
      setCompanyId(''); setPreview(null); setResult(null);
    })();
  }, [tenantId, _SA]);

  const _payload = () => ({
    tenant_id: tenantId,
    company_id: companyId || null,
    imported_only: importedOnly,
    status_filter: statusFilter || null,
    date_from: dateFrom || null,
    date_to: dateTo || null,
    // iter156eo: aceita "1013" (todas as parcelas), "1013.3" (parcela) ou intervalo 1013 → 1020
    number_from: numberFrom ? numberFrom.trim() : null,
    number_to: numberTo ? numberTo.trim() : (numberFrom ? numberFrom.trim() : null),
    hard_delete: hardDelete,
  });

  const runPreview = async () => {
    if (!tenantId) return toast.error('Selecione a empresa (tenant)');
    if (!importedOnly && !companyId && !statusFilter && !dateFrom && !dateTo && !numberFrom && !numberTo) {
      return toast.error('Informe ao menos 1 filtro (filial, status, importados, datas ou N).');
    }
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${_SA}/financial/bulk-delete/preview`, _payload(), { withCredentials: true });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha na preview');
    } finally { setBusy(false); }
  };

  const runDelete = async () => {
    if (!preview) return toast.error('Rode a preview antes.');
    const nDel = preview.total_after_number_filter ?? preview.total_matched_no_number ?? 0;
    if (nDel <= 0) return toast.info('Nada a excluir.');
    const ok = await confirmDialog({
      title: 'Excluir lancamentos financeiros?',
      message: `Confirma exclusao de ~${nDel} lancamento(s)? Modo: ${hardDelete ? 'HARD (remove do banco)' : 'SOFT (marca como cancelled)'}. Vendas com NF-e autorizada em producao NAO serao afetadas.`,
      confirmText: 'Excluir', danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${_SA}/financial/bulk-delete`, _payload(), { withCredentials: true });
      setResult(data);
      toast.success(`Excluidos: ${data.deleted} · Protegidos por NF-e: ${data.protected_by_nfe}`);
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha na exclusao');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="financial-bulk-delete-page">
      <div>
        <h1 className="text-3xl font-bold text-white">Exclusao em Massa - Lancamentos Financeiros</h1>
        <p className="text-slate-400 text-sm mt-1">Filtros combinaveis. Padrao = SOFT delete (status=cancelled). NF-e autorizada em producao nunca eh afetada.</p>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-slate-400 font-bold uppercase mb-1">Empresa (Tenant) *</label>
            <select value={tenantId} onChange={e => setTenantId(e.target.value)} className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-lg text-sm" data-testid="fbd-tenant">
              <option value="">— Selecione —</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.company_name || t.subdomain}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 font-bold uppercase mb-1">Filial (opcional)</label>
            <select value={companyId} onChange={e => setCompanyId(e.target.value)} className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-lg text-sm" data-testid="fbd-company">
              <option value="">— Todas as filiais —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.code || ''} {c.name}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <input type="checkbox" checked={importedOnly} onChange={e => setImportedOnly(e.target.checked)} data-testid="fbd-imported-only" />
              <span className="font-bold">Apenas importados via planilha</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-slate-400 font-bold uppercase mb-1">Status</label>
            {/* iter156dx: dropdown de status — cancelled acha soft-deleted para purgar via hard */}
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-lg text-sm" data-testid="fbd-status">
              <option value="">— Todos —</option>
              <option value="pending">Em aberto (pending)</option>
              <option value="paid">Pagas (paid)</option>
              <option value="cancelled">Ja canceladas (soft-deleted)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 font-bold uppercase mb-1">Data De</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-lg text-sm" data-testid="fbd-date-from" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 font-bold uppercase mb-1">Data Ate</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-lg text-sm" data-testid="fbd-date-to" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 font-bold uppercase mb-1">N do Lanc. De</label>
            <input value={numberFrom} onChange={e => setNumberFrom(e.target.value.replace(/[^\d.]/g, ''))} placeholder="ex: 1013 ou 1013.3" title="Codigo base traz todas as parcelas (1013.1, 1013.2...). Use ponto para uma parcela. Preencha 'ate' para intervalo." className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-lg text-sm" data-testid="fbd-number-from" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 font-bold uppercase mb-1">N do Lanc. Ate</label>
            <input value={numberTo} onChange={e => setNumberTo(e.target.value.replace(/[^\d.]/g, ''))} placeholder="ex: 1020 (vazio = so o 'de')" className="w-full px-3 py-2 bg-white/10 border border-white/20 text-white rounded-lg text-sm" data-testid="fbd-number-to" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-rose-300 cursor-pointer">
            <input type="checkbox" checked={hardDelete} onChange={e => setHardDelete(e.target.checked)} data-testid="fbd-hard" />
            <span className="font-bold">HARD delete (remove definitivo — nao recuperavel)</span>
          </label>
          {statusFilter === 'cancelled' && !hardDelete && (
            <p className="text-[11px] text-amber-300 italic">Dica: como voce filtrou "Ja canceladas", marque <b>HARD delete</b> para remover do BD de vez.</p>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={runPreview} disabled={busy || !tenantId} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white text-sm font-bold rounded-lg" data-testid="fbd-preview">
              {busy && !preview ? 'Buscando...' : 'Preview'}
            </button>
            <button onClick={runDelete} disabled={busy || !preview} className="px-4 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white text-sm font-bold rounded-lg" data-testid="fbd-execute">
              {busy && preview ? 'Excluindo...' : 'Executar Exclusao'}
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="fbd-preview-result">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold">Preview</h3>
            <div className="text-slate-300 text-sm">
              <span className="mr-4">Total sem filtro N: <b className="text-amber-300">{preview.total_matched_no_number}</b></span>
              <span>Total apos filtro N: <b className="text-emerald-300">{preview.total_after_number_filter}</b></span>
            </div>
          </div>
          {preview.note && <p className="text-[11px] text-slate-500 italic mb-2">{preview.note}</p>}
          {preview.sample?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-slate-200">
                <thead className="text-slate-400 uppercase text-[10px]">
                  <tr>
                    <th className="text-left py-1 px-2">Cod</th>
                    <th className="text-left py-1 px-2">Tipo</th>
                    <th className="text-left py-1 px-2">Status</th>
                    <th className="text-left py-1 px-2">Cliente/Fornecedor</th>
                    <th className="text-left py-1 px-2">Descricao</th>
                    <th className="text-left py-1 px-2">Vencimento</th>
                    <th className="text-right py-1 px-2">Valor</th>
                    <th className="text-center py-1 px-2">Importado?</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {preview.sample.map(s => {
                    // iter156dx: badge de status visivel para o operador saber se ja esta cancelado
                    const stCls = s.status === 'cancelled' ? 'bg-rose-900/40 text-rose-300'
                                : s.status === 'paid' ? 'bg-emerald-900/40 text-emerald-300'
                                : s.status === 'pending' ? 'bg-amber-900/40 text-amber-300'
                                : 'bg-slate-700/50 text-slate-300';
                    return (
                    <tr key={s.id}>
                      <td className="py-1 px-2 font-mono">{s.cod}</td>
                      <td className="py-1 px-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${s.type === 'receivable' ? 'bg-emerald-900/40 text-emerald-300' : 'bg-rose-900/40 text-rose-300'}`}>{s.type === 'receivable' ? 'RECEBER' : 'PAGAR'}</span></td>
                      <td className="py-1 px-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${stCls}`}>{s.status || '—'}</span></td>
                      <td className="py-1 px-2 truncate max-w-[200px]">{s.party_name || '—'}</td>
                      <td className="py-1 px-2 truncate max-w-[220px]">{s.description || '—'}</td>
                      <td className="py-1 px-2 font-mono">{s.due_date || '—'}</td>
                      <td className="py-1 px-2 text-right font-mono">R$ {Number(s.amount || 0).toFixed(2)}</td>
                      <td className="py-1 px-2 text-center">{s.created_by_import ? '✓' : ''}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
              <p className="text-[10px] text-slate-500 mt-2">Amostra dos 20 primeiros. O total real esta acima.</p>
            </div>
          ) : (
            <p className="text-slate-400 text-sm">Nenhum lancamento encontrado com esses filtros.</p>
          )}
        </div>
      )}

      {result && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-emerald-200" data-testid="fbd-result">
          <p className="font-bold">Resultado</p>
          <p className="text-sm">Encontrados: {result.matched} · Excluidos: {result.deleted} · Protegidos por NF-e: {result.protected_by_nfe} · Modo: {result.mode}</p>
        </div>
      )}
    </div>
  );
};

// iter77ct.105: SuperAdmin - Exportar Produtos por Filial + analise de duplicatas
const ProductExportPage = () => {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyExport, setBusyExport] = useState(false);

  const SA = `${API}/superadmin`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${SA}/tenants`, { withCredentials: true });
        setTenants(Array.isArray(data) ? data : (data?.data || []));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao carregar empresas');
      }
    })();
  }, [SA]);

  useEffect(() => {
    if (!tenantId) { setCompanies([]); setSelectedCompanyIds([]); setAnalysis(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${SA}/tenants/${tenantId}/companies?all=1`, { withCredentials: true });
        const items = Array.isArray(data) ? data : (data?.data || []);
        setCompanies(items);
        setSelectedCompanyIds(items.map(c => c.id));
        setAnalysis(null);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao carregar filiais');
      }
    })();
  }, [tenantId, SA]);

  const toggleCompany = (cid) => {
    setSelectedCompanyIds(prev => prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid]);
    setAnalysis(null);
  };
  const toggleAllCompanies = () => {
    setSelectedCompanyIds(prev => prev.length === companies.length ? [] : companies.map(c => c.id));
    setAnalysis(null);
  };

  const runAnalyze = async () => {
    if (!tenantId) { toast.error('Selecione uma empresa'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${SA}/products/export/analyze`, {
        tenant_id: tenantId,
        company_ids: selectedCompanyIds,
      }, { withCredentials: true });
      setAnalysis(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na analise');
    } finally { setBusy(false); }
  };

  const doExport = async (excludeDuplicates) => {
    if (!tenantId) { toast.error('Selecione uma empresa'); return; }
    setBusyExport(true);
    try {
      const resp = await axios.post(`${SA}/products/export/xlsx`, {
        tenant_id: tenantId,
        company_ids: selectedCompanyIds,
        exclude_duplicates: !!excludeDuplicates,
      }, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const cd = resp.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.href = url;
      a.download = m ? m[1] : `produtos_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exportacao gerada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao exportar');
    } finally { setBusyExport(false); }
  };

  return (
    <div className="space-y-6" data-testid="product-export-page">
      <div>
        <h2 className="text-2xl font-bold text-white">Exportar Produtos para Excel</h2>
        <p className="text-slate-400 text-sm">Selecione a empresa e as filiais. Um relatorio de duplicatas sera gerado antes da exportacao.</p>
      </div>

      {/* Filtros */}
      <div className="bg-[#16162a] border border-[#2a2a4a] rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Empresa (Tenant) *</label>
            <select
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
              className="w-full bg-slate-900 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="prod-export-tenant-select"
            >
              <option value="">— Selecione —</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.company_name || t.name || t.owner_name || '(sem nome)'} ({t.subdomain || t.slug || t.id.slice(-6)})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Filiais</label>
            <div className="flex items-center gap-2 flex-wrap p-2 bg-slate-900 border border-white/20 rounded-lg min-h-[42px] max-h-32 overflow-y-auto">
              {companies.length === 0 && <span className="text-slate-500 text-sm px-2">Nenhuma filial</span>}
              {companies.length > 0 && (
                <button
                  onClick={toggleAllCompanies}
                  className="text-xs px-2 py-0.5 rounded bg-violet-600 hover:bg-violet-500 text-white font-bold"
                  data-testid="prod-export-toggle-all-companies"
                >
                  {selectedCompanyIds.length === companies.length ? 'Desmarcar Todas' : 'Todas'}
                </button>
              )}
              {companies.map(c => (
                <label key={c.id} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded cursor-pointer transition ${selectedCompanyIds.includes(c.id) ? 'bg-cyan-600/20 border border-cyan-500/40 text-cyan-200' : 'bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700'}`} data-testid={`prod-export-company-${c.id}`}>
                  <input
                    type="checkbox"
                    checked={selectedCompanyIds.includes(c.id)}
                    onChange={() => toggleCompany(c.id)}
                    className="accent-cyan-500"
                  />
                  {c.name}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={runAnalyze}
            disabled={busy || !tenantId}
            className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold text-sm flex items-center gap-2"
            data-testid="prod-export-analyze-btn"
          >
            <Search size={14} />
            {busy ? 'Analisando...' : 'Analisar Duplicatas'}
          </button>
        </div>
      </div>

      {/* Analise */}
      {analysis && (
        <div className="bg-[#16162a] border border-[#2a2a4a] rounded-xl p-6 space-y-4" data-testid="prod-export-analysis">
          <h3 className="text-lg font-bold text-white">Relatorio de Analise</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-slate-800 rounded-lg p-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase">Total de Produtos</p>
              <p className="text-2xl font-black text-white" data-testid="prod-export-total">{analysis.total}</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <p className="text-[10px] font-bold text-amber-300 uppercase">SKU Duplicado</p>
              <p className="text-2xl font-black text-amber-300" data-testid="prod-export-dup-sku">{analysis.duplicate_sku_count}</p>
              <p className="text-[10px] text-amber-200/70">{analysis.duplicate_sku_groups} grupo(s)</p>
            </div>
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3">
              <p className="text-[10px] font-bold text-rose-300 uppercase">Nome Duplicado</p>
              <p className="text-2xl font-black text-rose-300" data-testid="prod-export-dup-name">{analysis.duplicate_name_count}</p>
              <p className="text-[10px] text-rose-200/70">{analysis.duplicate_name_groups} grupo(s)</p>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
              <p className="text-[10px] font-bold text-emerald-300 uppercase">Se Excluir Duplicados</p>
              <p className="text-2xl font-black text-emerald-300" data-testid="prod-export-unique">{analysis.unique_export_count}</p>
              <p className="text-[10px] text-emerald-200/70">produtos serao exportados</p>
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Um produto e considerado duplicado quando outro produto tem o <strong>mesmo SKU</strong> OU o <strong>mesmo Nome</strong> (case+acento insensitive).
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => doExport(false)}
              disabled={busyExport}
              className="flex-1 px-4 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white font-bold text-sm flex items-center justify-center gap-2"
              data-testid="prod-export-all-btn"
            >
              <Download size={14} />
              {busyExport ? 'Gerando...' : `Exportar TODOS (${analysis.total} produtos)`}
            </button>
            <button
              onClick={() => doExport(true)}
              disabled={busyExport || analysis.duplicate_count === 0}
              className="flex-1 px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-bold text-sm flex items-center justify-center gap-2"
              data-testid="prod-export-unique-btn"
            >
              <FileSpreadsheet size={14} />
              {busyExport ? 'Gerando...' : `Exportar SEM Duplicados (${analysis.unique_export_count} produtos)`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
